import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios, logout as authLogout } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import SubscriptionSettingsPanel from "../components/SubscriptionSettingsPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Heart, ArrowLeft, Home, User, Calendar, Menu, 
  MapPin, Sparkles, Target, Plus, Facebook, Twitter, Instagram, RefreshCw, BookOpen, Database,
  Camera, Upload, X, ImageIcon, UserCircle, Edit, ChevronRight, ChevronDown, CheckCircle2,
  Bell, Moon, Shield, Trash2, LogOut, Languages, UserCog, Palette, Globe, Settings, Lightbulb, Scale, Rocket, Coins, Headset, ExternalLink, Clock, AlertCircle, MessageSquare, Paperclip, Mail, FileText,
  Pencil, Phone, AlertTriangle, Check, CheckCheck, Loader2, KeyRound
} from "lucide-react";
import { Toaster, toast } from "sonner";
import DataSourcesManager from "../components/DataSourcesManager";
import LearningResourcesManager from "../components/LearningResourcesManager";
import CoinPurchaseModal from "../components/CoinPurchaseModal";
import ImageAttachPicker from "../components/ImageAttachPicker";
import AddPersonaModal from "../components/AddPersonaModal";
import { TcAgreementCard } from "../components/TcAgreementCard";
import { useTheme } from "../contexts/ThemeContext";
import { TIMEZONES, getTimezoneLabel } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import NotificationBell from "../components/notifications/NotificationBell";

// Supported countries for safety resources
const SUPPORTED_COUNTRIES = [
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
  { code: "IE", name: "Ireland", flag: "🇮🇪" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "FI", name: "Finland", flag: "🇫🇮" },
  { code: "BE", name: "Belgium", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭" },
  { code: "AT", name: "Austria", flag: "🇦🇹" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "SG", name: "Singapore", flag: "🇸🇬" },
  { code: "HK", name: "Hong Kong", flag: "🇭🇰" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "EU_DEFAULT", name: "Other EU Country", flag: "🇪🇺" },
];

// Calculate age from birth date
const calculateAge = (birthDate) => {
  if (!birthDate) return "";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// ==================== EDIT ACCOUNT MODAL ====================
const EditAccountModal = ({ isOpen, onClose, accountData, onSaved, isCyber }) => {
  const [name, setName] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [nameError, setNameError] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkingName, setCheckingName] = useState(false);

  useEffect(() => {
    if (isOpen && accountData) {
      setName(accountData.name || "");
      setState(accountData.state || "");
      setCountry(accountData.country || "");
      setNameError("");
    }
  }, [isOpen, accountData]);

  const checkNameAvailability = async (nameVal) => {
    if (!nameVal.trim() || nameVal.trim() === accountData.name) { setNameError(""); return true; }
    setCheckingName(true);
    try {
      const res = await authAxios.get(`/account/check-name?name=${encodeURIComponent(nameVal.trim())}`);
      if (!res.data.available) { setNameError("This name is already taken"); return false; }
      setNameError(""); return true;
    } catch { setNameError("Unable to check name"); return false; }
    finally { setCheckingName(false); }
  };

  const handleSave = async () => {
    const trimName = name.trim();
    if (trimName.length < 2) { setNameError("Name must be at least 2 characters"); return; }
    const nameOk = await checkNameAvailability(trimName);
    if (!nameOk) return;
    setSaving(true);
    try {
      const payload = {};
      if (trimName !== accountData.name) payload.name = trimName;
      if (state.trim() !== (accountData.state || "")) payload.state = state.trim();
      if (country.trim() !== (accountData.country || "")) payload.country = country.trim();
      if (Object.keys(payload).length === 0) { onClose(); return; }
      const res = await authAxios.put("/account/details", payload);
      toast.success("Account details updated");
      onSaved(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to update";
      if (detail.includes("already taken")) { setNameError(detail); } else { toast.error(detail); }
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  const accent = isCyber ? "cyan-400" : "divine-gold";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#161616] border rounded-xl w-full max-w-md mx-4 p-6 ${isCyber ? "border-cyan-400/20" : "border-white/10"}`} onClick={e => e.stopPropagation()} data-testid="edit-account-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Edit Account Details</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="edit-modal-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-white/60 text-sm mb-1 block">Display Name</label>
            <input data-testid="edit-name-input" type="text" value={name} onChange={e => { setName(e.target.value); setNameError(""); }} onBlur={() => checkNameAvailability(name)}
              className={`w-full bg-white/5 border rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 ${nameError ? "border-red-500 focus:ring-red-500" : `border-white/10 focus:ring-${accent}/50`}`} placeholder="Your display name" />
            {checkingName && <p className="text-white/40 text-xs mt-1">Checking availability...</p>}
            {nameError && <p className="text-red-400 text-xs mt-1" data-testid="name-error">{nameError}</p>}
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">State / Province</label>
            <input data-testid="edit-state-input" type="text" value={state} onChange={e => setState(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-divine-gold/50" placeholder="e.g. NSW, California" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Country</label>
            <input data-testid="edit-country-input" type="text" value={country} onChange={e => setCountry(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-divine-gold/50" placeholder="e.g. Australia" />
          </div>
        </div>
        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-amber-300 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Phone and email changes require email verification and cannot be edited here.</span>
          </p>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white hover:bg-white/5" data-testid="edit-modal-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !!nameError} className={`flex-1 ${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold text-black hover:bg-divine-gold/90"}`} data-testid="edit-modal-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== CHANGE PHONE MODAL ====================
const ChangePhoneModal = ({ isOpen, onClose, currentPhone, isCyber }) => {
  const [newPhone, setNewPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (isOpen) { setNewPhone(""); setSent(false); } }, [isOpen]);

  const handleSubmit = async () => {
    if (!newPhone.trim()) { toast.error("Enter a phone number"); return; }
    setSending(true);
    try {
      await authAxios.post("/account/request-phone-change", { new_phone: newPhone.trim() });
      setSent(true); toast.success("Confirmation email sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send confirmation"); }
    finally { setSending(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#161616] border rounded-xl w-full max-w-md mx-4 p-6 ${isCyber ? "border-cyan-400/20" : "border-white/10"}`} onClick={e => e.stopPropagation()} data-testid="change-phone-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Change Phone Number</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><Mail className="w-6 h-6 text-green-400" /></div>
            <p className="text-white text-base mb-2">Check your email</p>
            <p className="text-white/50 text-sm">We sent a confirmation link to your current email. Click it to confirm the phone number change.</p>
            <Button variant="outline" onClick={onClose} className="mt-6 border-white/20 text-white hover:bg-white/5" data-testid="phone-done-btn">Done</Button>
          </div>
        ) : (
          <>
            {currentPhone && <p className="text-white/50 text-sm mb-3">Current: <span className="text-white/70">{currentPhone}</span></p>}
            <label className="text-white/60 text-sm mb-1 block">New Phone Number</label>
            <input data-testid="new-phone-input" type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-divine-gold/50" placeholder="+61 400 000 000" />
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-amber-300 text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>A confirmation email will be sent to your current email address. You must click the link to complete the change.</span>
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleSubmit} disabled={sending || !newPhone.trim()} className={`flex-1 ${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold text-black hover:bg-divine-gold/90"}`} data-testid="send-phone-confirm-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Confirmation
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== CHANGE EMAIL MODAL ====================
const ChangeEmailModal = ({ isOpen, onClose, isCyber }) => {
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (isOpen) { setNewEmail(""); setSent(false); } }, [isOpen]);

  const handleSubmit = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      await authAxios.post("/account/request-email-change", { new_email: newEmail.trim() });
      setSent(true); toast.success("Confirmation email sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send confirmation"); }
    finally { setSending(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#161616] border rounded-xl w-full max-w-md mx-4 p-6 ${isCyber ? "border-cyan-400/20" : "border-white/10"}`} onClick={e => e.stopPropagation()} data-testid="change-email-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Change Email Address</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><Mail className="w-6 h-6 text-green-400" /></div>
            <p className="text-white text-base mb-2">Check your current email</p>
            <p className="text-white/50 text-sm">We sent a confirmation link to your current email address. Click it to proceed, then validate your new email.</p>
            <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-purple-300 text-xs">This is a 2-step process: confirm on old email, then validate on new email.</p>
            </div>
            <Button variant="outline" onClick={onClose} className="mt-6 border-white/20 text-white hover:bg-white/5" data-testid="email-done-btn">Done</Button>
          </div>
        ) : (
          <>
            <label className="text-white/60 text-sm mb-1 block">New Email Address</label>
            <input data-testid="new-email-input" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-divine-gold/50" placeholder="newemail@example.com" />
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-amber-300 text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>A 2-step verification is required: (1) Confirm on your current email, (2) Validate on your new email. You will be logged out after the change.</span>
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleSubmit} disabled={sending || !newEmail.trim()} className={`flex-1 ${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold text-black hover:bg-divine-gold/90"}`} data-testid="send-email-confirm-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Confirmation
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== CHANGE HISTORY PANEL ====================
const ChangeHistoryPanel = ({ isCyber }) => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authAxios.get("/account/change-history");
        setChanges(res.data.changes || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " +
           d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const changeIcon = (type) => {
    switch (type) {
      case "Name": return <User className="w-3.5 h-3.5" />;
      case "Email": return <Mail className="w-3.5 h-3.5" />;
      case "Phone": return <Phone className="w-3.5 h-3.5" />;
      case "State": return <MapPin className="w-3.5 h-3.5" />;
      case "Country": return <Globe className="w-3.5 h-3.5" />;
      default: return <Settings className="w-3.5 h-3.5" />;
    }
  };

  const displayChanges = expanded ? changes : changes.slice(0, 5);

  return (
    <div className={`rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass"}`} data-testid="change-history-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Change History</h3>
        <span className="text-white/30 text-xs">{changes.length} event{changes.length !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
        </div>
      ) : changes.length === 0 ? (
        <div className="text-center py-6">
          <Shield className="w-8 h-8 text-white/10 mx-auto mb-2" />
          <p className="text-white/30 text-sm">No account changes recorded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayChanges.map((c, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg" data-testid={`change-log-entry-${i}`}>
              <div className={`mt-0.5 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>{changeIcon(c.change_type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium">{c.change_type} updated</span>
                  <span className="text-white/20 text-xs">&bull;</span>
                  <span className="text-white/40 text-xs">{formatDate(c.timestamp)}</span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs">
                  <span className="text-red-400/70 line-through truncate max-w-[140px]">{c.old_value || "empty"}</span>
                  <span className="text-white/20 mx-1">&rarr;</span>
                  <span className="text-green-400/80 truncate max-w-[140px]">{c.new_value || "empty"}</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-[10px] text-white/25">
                  <span>IP: {c.partial_ip || "—"}</span>
                  {c.ip_country && <span>{c.ip_country}</span>}
                </div>
              </div>
            </div>
          ))}
          {changes.length > 5 && (
            <button onClick={() => setExpanded(!expanded)} className="w-full text-center py-2 text-white/40 hover:text-white/60 text-xs transition-colors" data-testid="change-history-toggle">
              {expanded ? "Show less" : `Show all ${changes.length} changes`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ProfileSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { theme, changeTheme, resetToDefault } = useTheme();
  const isCyber = theme === "agent_eros_1";
  const [loading, setLoading] = useState(false);
  
  // Avatar upload states
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [showAddPersonaModal, setShowAddPersonaModal] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  
  // Settings states
  const [notifications, setNotifications] = useState(user?.email_prefs?.notifications !== false);
  const [marketingEmails, setMarketingEmails] = useState(user?.email_prefs?.marketing === true);
  const [ticketUpdateEmails, setTicketUpdateEmails] = useState(user?.email_prefs?.ticket_updates !== false);
  const [forumReplyEmails, setForumReplyEmails] = useState(user?.email_prefs?.forum_replies !== false);
  const [darkMode, setDarkMode] = useState(user?.dark_mode !== false);
  const [language, setLanguage] = useState(user?.language || "english");
  const [timezone, setTimezone] = useState(user?.timezone || "UTC");
  const [affiliatePaymentEmails, setAffiliatePaymentEmails] = useState(true);
  const [affiliateWeeklyDigest, setAffiliateWeeklyDigest] = useState(false);
  const [countryConfirmed, setCountryConfirmed] = useState(user?.country_confirmed || "");
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);

  // Load affiliate-payment email opt-in on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/notification-preferences`,
          {
            credentials: "include",
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setAffiliatePaymentEmails(!!data.affiliate_payment_emails);
          setAffiliateWeeklyDigest(!!data.affiliate_weekly_digest);
        }
      } catch { /* silent — default is opt-in */ }
    })();
  }, []);
  const [savingCountry, setSavingCountry] = useState(false);
  
  // Account details state
  const [accountData, setAccountData] = useState({
    name: user?.name || "", email: user?.email || "",
    phone_number: user?.phone_number || "", state: user?.state || "", country: user?.country || "",
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  
  // Coin balance states
  const [coinBalance, setCoinBalance] = useState(null);
  const [showCoinPurchaseModal, setShowCoinPurchaseModal] = useState(false);
  
  // Claimed buttons state
  const [claimedButtons, setClaimedButtons] = useState([]);
  const [claimingButton, setClaimingButton] = useState(null);
  
  // Fast Start coin rewards configuration
  const FAST_START_REWARDS = {
    persona_profile: { coins: 1200, reference: "Fast Start persona profile" },
    relationship_profile: { coins: 1200, reference: "Fast Start relationship profile" },
    first_contact: { coins: 600, reference: "Fast Start 1st Agent Eros Chat" },
    learning_library: { coins: 1200, reference: "Fast Start Learning Libraries" },
    style_card: { coins: 1200, reference: "Fast Start Style-Card" },
    compatibility: { coins: 600, reference: "Fast Start Compatibility" },
    fast_start_complete: { coins: 3000, reference: "Fast Start Complete" }
  };
  
  // Theme-aware select styling with better contrast
  const selectClassName = isCyber
    ? "w-full mt-2 bg-divine-black border border-cyan-400/50 text-white rounded-md px-3 py-2 text-sm focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30 focus:outline-none"
    : "w-full mt-2 bg-divine-black border border-divine-gold/50 text-white rounded-md px-3 py-2 text-sm focus:border-divine-gold focus:ring-2 focus:ring-divine-gold/30 focus:outline-none";
  const [socialConnections, setSocialConnections] = useState([]);
  const [learningResource, setLearningResource] = useState(null);
  const [generatingResource, setGeneratingResource] = useState(false);
  
  // Support ticket states
  const [supportTickets, setSupportTickets] = useState([]);
  const [loadingSupportTickets, setLoadingSupportTickets] = useState(false);
  const [unreadTicketCount, setUnreadTicketCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("ALL");
  const [updatingTicketStatus, setUpdatingTicketStatus] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  
  // Check for tab query parameter in URL
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  const taskFromUrl = searchParams.get('task');
  const [activeTab, setActiveTab] = useState(tabFromUrl || location.state?.activeTab || "profile");
  
  // Map task IDs to panel names
  const taskIdToPanelName = {
    "persona_profile": "persona",
    "relationship_profile": "relationship",
    "first_contact_agent_eros": "firstContact",
    "learning_library": "learningLibrary",
    "style_card": "styleCard",
    "compatibility_report": "compatibilityReport"
  };
  
  // Auto-expand task panel from URL query parameter
  useEffect(() => {
    if (taskFromUrl && taskIdToPanelName[taskFromUrl]) {
      setActiveTaskPanel(taskIdToPanelName[taskFromUrl]);
    }
  }, [taskFromUrl]);
  
  // User Persona states
  const [userPersona, setUserPersona] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState("");
  const [savingPersona, setSavingPersona] = useState(false);
  const [activeTaskPanel, setActiveTaskPanel] = useState(null); // Single panel state for accordion behavior
  const [userPersonaHasLearning, setUserPersonaHasLearning] = useState(false);
  const [topMatchHasLearning, setTopMatchHasLearning] = useState(false);
  const [userPersonaHasStyleCard, setUserPersonaHasStyleCard] = useState(false);
  const [topMatchHasStyleCard, setTopMatchHasStyleCard] = useState(false);
  const [topMatchHasCompatibilityReport, setTopMatchHasCompatibilityReport] = useState(false);
  
  // Toggle task panel - closes others, opens selected (or closes if already open)
  const panelIds = { persona: 'fast1', relationship: 'fast2', firstContact: 'fast3', learningLibrary: 'fast4', styleCard: 'fast5', compatibilityReport: 'fast6' };
  const toggleTaskPanel = (panelName) => {
    setActiveTaskPanel(prev => {
      const next = prev === panelName ? null : panelName;
      if (next) {
        setTimeout(() => {
          document.getElementById(panelIds[next])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      return next;
    });
  };
  
  // Fast Start Tasks completion state (persisted to server)
  const [completedTasks, setCompletedTasks] = useState({});
  const [tasksInitiallyLoaded, setTasksInitiallyLoaded] = useState(false);
  const syncedTasksRef = useRef(new Set());
  
  // Get the highest % complete relationship match (excluding user_persona)
  const topRelationshipMatch = allMatches
    .filter(m => m.match_type !== "user_persona")
    .sort((a, b) => (b.profile_completion || 0) - (a.profile_completion || 0))[0] || null;
  
  // Calculate learning library completion percentage
  const learningLibraryCompletion = (userPersonaHasLearning ? 50 : 0) + (topMatchHasLearning ? 50 : 0);
  
  // Calculate style card completion percentage
  const styleCardCompletion = (userPersonaHasStyleCard ? 50 : 0) + (topMatchHasStyleCard ? 50 : 0);
  
  // Task completion status helpers
  const isTaskComplete = (taskId) => completedTasks[taskId]?.completed === true;
  const isPersonaProfileComplete = isTaskComplete("persona_profile") || userPersona?.profile_completion === 100;
  const isRelationshipProfileComplete = isTaskComplete("relationship_profile") || topRelationshipMatch?.profile_completion === 100;
  const isFirstContactComplete = isTaskComplete("first_contact_agent_eros");
  const isLearningLibraryComplete = isTaskComplete("learning_library") || learningLibraryCompletion === 100;
  const isStyleCardComplete = isTaskComplete("style_card") || styleCardCompletion === 100;
  const isCompatibilityReportComplete = isTaskComplete("compatibility_report") || topMatchHasCompatibilityReport;
  
  // All tasks complete check (for Fast Start Complete bonus)
  const isAllTasksComplete = isPersonaProfileComplete && 
    isRelationshipProfileComplete && 
    isFirstContactComplete && 
    isLearningLibraryComplete && 
    isStyleCardComplete && 
    isCompatibilityReportComplete;
  
  // Calculate total Fast Start completion percentage
  const totalFastStartCompletion = Math.round((
    (userPersona?.profile_completion || 0) +
    (topRelationshipMatch?.profile_completion || 0) +
    (isFirstContactComplete ? 100 : 0) +
    learningLibraryCompletion +
    styleCardCompletion +
    (isCompatibilityReportComplete ? 100 : 0)
  ) / 6);
  
  const [profile, setProfile] = useState({
    name: user?.name || "",
    bio: "",
    birth_date: "",
    birth_time: "",
    birth_location: "",
    age: null,
    interests: [],
    relationship_goals: "",
    
    // Extended fields
    sex: "",
    pronouns: "",
    sexual_orientation: "",
    ethnicity: "",
    
    // Physical Appearance
    height: "",
    weight: "",
    body_type: "",
    hair_colour: "",
    eye_colour: "",
    complexion: "",
    
    // Relationship Context
    current_relationship_status: "",
    relationship_type: "",
    current_partner: "",
    ex_partner_details: "",
    
    // Location
    living_location: "",
    
    // Interests & Lifestyle
    attraction_types: "",
    fantasies_dreams: "",
    hobbies_passions: "",
    vibe_style_presentation: "",
    
    // Work & Career
    work_details: "",
    work_ambition_goals: "",
    work_history: "",
    
    // Family
    parent_details: "",
    sibling_details: "",
    children_details: "",
    family_style_type_details: "",
    pets_details: "",
    
    // Social Dynamics
    people_admire: "",
    people_dislike: "",
    boundaries: "",
    
    // Values
    political_views: "",
    spiritual_views: "",
    
    // Communication
    primary_love_lang: "",
    secondary_love_lang: "",
    communication_style: "",
    texting_style: "",
    
    // Web Links
    web_links: ""
  });
  const [interestInput, setInterestInput] = useState("");

  useEffect(() => {
    fetchProfile();
    fetchSocialConnections();
    fetchLearningResource();
    fetchUserPersona();
    fetchAllMatches();
    fetchCompletedTasks();
    fetchCoinBalance();
    fetchClaimedButtons();
    fetchAccountDetails();
  }, []);

  // Fetch account details
  const fetchAccountDetails = async () => {
    try {
      const res = await authAxios.get("/account/details");
      setAccountData(res.data);
    } catch (err) {
      console.error("Failed to fetch account details:", err);
    }
  };

  const handleAccountSaved = (data) => {
    setAccountData(prev => ({ ...prev, name: data.name, state: data.state, country: data.country }));
    if (data.name && data.name !== user?.name) {
      setUser({ ...user, name: data.name });
    }
  };

  // Fetch coin balance
  const fetchCoinBalance = async () => {
    try {
      const response = await authAxios.get("/credits/balance");
      setCoinBalance(response.data.balance);
    } catch (error) {
      console.error("Failed to fetch coin balance:", error);
    }
  };

  // Fetch claimed buttons
  const fetchClaimedButtons = async () => {
    try {
      const response = await authAxios.get("/credits/claimed-buttons");
      setClaimedButtons(response.data.claimed || []);
    } catch (error) {
      console.error("Failed to fetch claimed buttons:", error);
    }
  };

  // Claim coins from a Fast Start task
  const handleClaimCoins = async (taskKey) => {
    const reward = FAST_START_REWARDS[taskKey];
    if (!reward) return;
    
    setClaimingButton(taskKey);
    try {
      const response = await authAxios.post("/credits/claim-buttons", {
        reference: reward.reference,
        coins: reward.coins
      });
      
      // Update coin balance
      setCoinBalance(response.data.new_balance);
      
      // Add to claimed list
      setClaimedButtons(prev => [...prev, reward.reference]);
      
      // Show success toast
      toast.success(`${reward.coins.toLocaleString()} COINS CLAIMED`, {
        style: {
          background: 'linear-gradient(135deg, #d4af37 0%, #f5d678 50%, #d4af37 100%)',
          color: '#1a1a1a',
          fontWeight: 'bold',
          border: '2px solid #f5d678'
        }
      });
    } catch (error) {
      console.error("Failed to claim coins:", error);
      toast.error(error.response?.data?.detail || "Failed to claim coins");
    } finally {
      setClaimingButton(null);
    }
  };

  // Check if a reward has been claimed
  const isRewardClaimed = (taskKey) => {
    const reward = FAST_START_REWARDS[taskKey];
    return reward && claimedButtons.includes(reward.reference);
  };

  // Fetch completed tasks from server
  const fetchCompletedTasks = async () => {
    try {
      const response = await authAxios.get("/profile/fast-start-tasks");
      const tasks = response.data.tasks || {};
      setCompletedTasks(tasks);
      setTasksInitiallyLoaded(true);
      // Pre-populate synced ref with already-completed tasks
      Object.keys(tasks).forEach(id => {
        if (tasks[id]?.completed) syncedTasksRef.current.add(id);
      });
    } catch (error) {
      console.error("Error fetching completed tasks:", error);
    }
  };

  // Mark a task as complete on the server
  const markTaskComplete = async (taskId, metadata = null) => {
    try {
      const body = metadata ? { metadata } : {};
      await authAxios.put(`/profile/fast-start-tasks/${taskId}`, body);
      setCompletedTasks(prev => ({
        ...prev,
        [taskId]: { completed: true, completed_at: new Date().toISOString(), ...body }
      }));
      return true;
    } catch (error) {
      console.error(`Error marking task ${taskId} complete:`, error);
      return false;
    }
  };

  // Auto-sync data-driven task completions to server (runs once per data change, not on completedTasks changes)
  useEffect(() => {
    if (!tasksInitiallyLoaded || (!userPersona && !topRelationshipMatch)) return;

    const syncTaskCompletions = async () => {
      // Persona Profile: Auto-complete when 100%
      if (userPersona?.profile_completion === 100 && !syncedTasksRef.current.has("persona_profile")) {
        syncedTasksRef.current.add("persona_profile");
        await markTaskComplete("persona_profile", { auto_completed: true });
      }
      
      // Relationship Profile: Auto-complete when 100%
      if (topRelationshipMatch?.profile_completion === 100 && !syncedTasksRef.current.has("relationship_profile")) {
        syncedTasksRef.current.add("relationship_profile");
        await markTaskComplete("relationship_profile", { auto_completed: true });
      }
      
      // Learning Library: Auto-complete when both have learning data
      if (learningLibraryCompletion === 100 && !syncedTasksRef.current.has("learning_library")) {
        syncedTasksRef.current.add("learning_library");
        await markTaskComplete("learning_library", { auto_completed: true });
      }
      
      // Style Card: Auto-complete when both have style cards
      if (styleCardCompletion === 100 && !syncedTasksRef.current.has("style_card")) {
        syncedTasksRef.current.add("style_card");
        await markTaskComplete("style_card", { auto_completed: true });
      }
      
      // Compatibility Report: Auto-complete when report exists
      if (topMatchHasCompatibilityReport && !syncedTasksRef.current.has("compatibility_report")) {
        syncedTasksRef.current.add("compatibility_report");
        await markTaskComplete("compatibility_report", { auto_completed: true });
      }
    };
    
    syncTaskCompletions();
  }, [tasksInitiallyLoaded, userPersona, topRelationshipMatch, learningLibraryCompletion, styleCardCompletion, topMatchHasCompatibilityReport]);

  // Fetch learning status and style card status when persona and matches are loaded
  useEffect(() => {
    const fetchLearningAndStyleStatus = async () => {
      // Check user persona learning data
      if (userPersona?.match_id) {
        try {
          const response = await authAxios.get(`/learning-profiles/has-data/relationship/${userPersona.match_id}`);
          setUserPersonaHasLearning(response.data.has_data);
        } catch (error) {
          console.error("Error checking user persona learning status:", error);
          setUserPersonaHasLearning(false);
        }
        
        // Check user persona style card
        try {
          const response = await authAxios.get(`/data-sources?owner_type=relationship&owner_id=${userPersona.match_id}`);
          const hasStyleCard = response.data.some(source => 
            source.name?.toUpperCase().includes('USER_STYLE_CARD') || 
            source.title?.toUpperCase().includes('USER_STYLE_CARD')
          );
          setUserPersonaHasStyleCard(hasStyleCard);
        } catch (error) {
          console.error("Error checking user persona style card:", error);
          setUserPersonaHasStyleCard(false);
        }
      }
      
      // Check top relationship match learning data
      if (topRelationshipMatch?.match_id) {
        try {
          const response = await authAxios.get(`/learning-profiles/has-data/relationship/${topRelationshipMatch.match_id}`);
          setTopMatchHasLearning(response.data.has_data);
        } catch (error) {
          console.error("Error checking top match learning status:", error);
          setTopMatchHasLearning(false);
        }
        
        // Check top match style card
        try {
          const response = await authAxios.get(`/data-sources?owner_type=relationship&owner_id=${topRelationshipMatch.match_id}`);
          const hasStyleCard = response.data.some(source => 
            source.name?.toUpperCase().includes('MATCH_STYLE_CARD') || 
            source.title?.toUpperCase().includes('MATCH_STYLE_CARD')
          );
          setTopMatchHasStyleCard(hasStyleCard);
        } catch (error) {
          console.error("Error checking top match style card:", error);
          setTopMatchHasStyleCard(false);
        }
        
        // Check top match compatibility report
        try {
          const response = await authAxios.get(`/matches/${topRelationshipMatch.match_id}`);
          // The analysis field contains the compatibility report
          setTopMatchHasCompatibilityReport(!!response.data.analysis);
        } catch (error) {
          console.error("Error checking compatibility report:", error);
          setTopMatchHasCompatibilityReport(false);
        }
      }
    };
    
    fetchLearningAndStyleStatus();
  }, [userPersona, topRelationshipMatch]);

  // Sync settings state with user data
  useEffect(() => {
    if (user) {
      setLanguage(user.language || "english");
      setTimezone(user.timezone || "UTC");
      setCountryConfirmed(user.country_confirmed || "");
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await authAxios.get("/profile");
      if (response.data.profile) {
        setProfile(prev => ({
          ...prev,
          ...response.data.profile,
          name: response.data.name || prev.name
        }));
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchSocialConnections = async () => {
    try {
      const response = await authAxios.get("/social/connections");
      setSocialConnections(response.data);
    } catch (error) {
      console.error("Error fetching social connections:", error);
    }
  };

  const fetchLearningResource = async () => {
    try {
      const response = await authAxios.get("/profile/learning-resource");
      setLearningResource(response.data.content);
    } catch (error) {
      console.error("Error fetching learning resource:", error);
    }
  };

  const fetchUserPersona = async () => {
    try {
      const response = await authAxios.get("/matches/user-persona");
      if (response.data) {
        setUserPersona(response.data);
        setSelectedPersonaId(response.data.match_id);
      }
    } catch (error) {
      console.error("Error fetching user persona:", error);
    }
  };

  const fetchAllMatches = async () => {
    try {
      const response = await authAxios.get("/matches");
      setAllMatches(response.data);
    } catch (error) {
      console.error("Error fetching matches:", error);
    }
  };

  const handleSetUserPersona = async (matchId) => {
    if (!matchId || matchId === selectedPersonaId) return;
    
    setSavingPersona(true);
    try {
      await authAxios.post("/matches/set-user-persona", { match_id: matchId });
      toast.success("Your persona has been updated!");
      setSelectedPersonaId(matchId);
      // Refresh persona data
      await fetchUserPersona();
      await fetchAllMatches();
    } catch (error) {
      console.error("Error setting user persona:", error);
      toast.error("Failed to update persona");
    } finally {
      setSavingPersona(false);
    }
  };

  const handleGenerateLearningResource = async () => {
    setGeneratingResource(true);
    try {
      const response = await authAxios.post("/profile/learning-resource", {});
      setLearningResource(response.data.content);
      toast.success("Learning resource generated!");
      setActiveTab("learning");
    } catch (error) {
      console.error("Error generating learning resource:", error);
      toast.error("Failed to generate learning resource");
    } finally {
      setGeneratingResource(false);
    }
  };

  // Support ticket functions
  const fetchSupportTickets = async () => {
    setLoadingSupportTickets(true);
    try {
      const response = await authAxios.get("/helpdesk/tickets");
      setSupportTickets(response.data.tickets || []);
    } catch (error) {
      console.error("Error fetching support tickets:", error);
      if (error.response?.status !== 401) {
        toast.error("Failed to load tickets");
      }
    } finally {
      setLoadingSupportTickets(false);
    }
  };
  
  const fetchUnreadTicketCount = async () => {
    try {
      const response = await authAxios.get("/helpdesk/unread-count");
      setUnreadTicketCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };
  
  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await authAxios.get(`/helpdesk/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      // Mark ticket as read in local list (removes unread dot without refetch)
      setSupportTickets(prev => prev.map(t => 
        t.ticket_id === ticketId ? { ...t, unread_by_user: false } : t
      ));
      // Refresh unread count after viewing
      fetchUnreadTicketCount();
    } catch (error) {
      console.error("Error fetching ticket detail:", error);
      // Don't toast on 401 — the interceptor handles token refresh transparently
      if (error.response?.status !== 401) {
        toast.error("Failed to load ticket details");
      }
    }
  };
  
  const handleTicketReply = async () => {
    if (!selectedTicket || !ticketReply.trim()) return;
    setSendingReply(true);
    try {
      const payload = {
        message: ticketReply.trim(),
        attachments: replyAttachments.map(a => ({ filename: a.filename, data: a.data }))
      };
      await authAxios.post(`/helpdesk/tickets/${selectedTicket.ticket_id}/reply`, payload);
      toast.success("Reply sent!");
      setTicketReply("");
      setReplyAttachments([]);
      // Refresh ticket detail
      await fetchTicketDetail(selectedTicket.ticket_id);
      // Refresh ticket list
      await fetchSupportTickets();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };
  
  const handleMarkAllTicketsRead = async () => {
    if (markingAllRead) return;
    const anyUnread = supportTickets.some(t => t.unread_by_user);
    if (!anyUnread) {
      toast.info("All tickets are already read");
      return;
    }
    setMarkingAllRead(true);
    try {
      const res = await authAxios.post("/helpdesk/tickets/mark-all-read");
      const n = res.data?.marked_read ?? 0;
      setSupportTickets(prev => prev.map(t => ({ ...t, unread_by_user: false })));
      setUnreadTicketCount(0);
      toast.success(n > 0 ? `${n} ticket${n === 1 ? "" : "s"} marked as read` : "All caught up");
    } catch (error) {
      console.error("Error marking all tickets read:", error);
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleTicketStatusChange = async (newStatus) => {
    if (!selectedTicket || newStatus === selectedTicket.status) return;
    setUpdatingTicketStatus(true);
    try {
      await authAxios.patch(`/helpdesk/tickets/${selectedTicket.ticket_id}/status`, { status: newStatus });
      toast.success(`Ticket ${newStatus.toLowerCase()}`);
      setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : prev);
      await fetchSupportTickets();
    } catch (error) {
      console.error("Error updating ticket status:", error);
      const detail = error.response?.data?.detail || "Failed to update status";
      toast.error(detail);
    } finally {
      setUpdatingTicketStatus(false);
    }
  };

  // Fetch support data when support tab is active
  useEffect(() => {
    if (activeTab === "support") {
      fetchSupportTickets();
    }
    // Always fetch unread count on mount
    fetchUnreadTicketCount();
  }, [activeTab]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await authAxios.put("/profile", profile);
      toast.success("Profile blessed by Eros!");
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const addInterest = () => {
    if (interestInput.trim() && !profile.interests.includes(interestInput.trim())) {
      setProfile(prev => ({
        ...prev,
        interests: [...prev.interests, interestInput.trim()]
      }));
      setInterestInput("");
    }
  };

  const removeInterest = (interest) => {
    setProfile(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest)
    }));
  };

  // Avatar upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarUpload = async () => {
    if (!avatarPreview) {
      toast.error("Please select an image first");
      return;
    }

    setUploadingAvatar(true);
    try {
      // Send base64 image to backend
      const response = await authAxios.put("/profile", { picture: avatarPreview });

      // Update user context with new picture
      if (setUser) {
        setUser(prev => ({ ...prev, picture: avatarPreview }));
      }

      toast.success(isCyber ? "Avatar uploaded successfully!" : "Photo blessed by Eros!");
      setAvatarModalOpen(false);
      setAvatarPreview(null);
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Failed to upload avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await authAxios.put("/profile", { picture: null });

      if (setUser) {
        setUser(prev => ({ ...prev, picture: null }));
      }

      toast.success("Avatar removed");
      setAvatarModalOpen(false);
      setAvatarPreview(null);
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast.error("Failed to remove avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Settings handlers
  const handleLanguageChange = async (newLanguage) => {
    setLanguage(newLanguage);
    setSavingLanguage(true);
    
    try {
      await authAxios.put("/profile", { language: newLanguage });
      setUser({ ...user, language: newLanguage });
      toast.success("Language preference updated");
    } catch (error) {
      console.error("Failed to update language:", error);
      toast.error("Failed to update language");
      setLanguage(user?.language || "english");
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleTimezoneChange = async (newTimezone) => {
    setTimezone(newTimezone);
    setSavingTimezone(true);
    
    try {
      await authAxios.put("/profile", { timezone: newTimezone });
      setUser({ ...user, timezone: newTimezone });
      toast.success(`Timezone updated to ${getTimezoneLabel(newTimezone)}`);
    } catch (error) {
      console.error("Failed to update timezone:", error);
      toast.error("Failed to update timezone");
      setTimezone(user?.timezone || "UTC");
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleCountryChange = async (newCountry) => {
    if (newCountry === "clear") {
      setSavingCountry(true);
      try {
        await authAxios.delete("/profile/country");
        setCountryConfirmed("");
        setUser({ ...user, country_confirmed: null });
        toast.success("Country cleared");
      } catch (error) {
        console.error("Failed to clear country:", error);
        toast.error("Failed to clear country");
      } finally {
        setSavingCountry(false);
      }
      return;
    }
    
    setCountryConfirmed(newCountry);
    setSavingCountry(true);
    
    try {
      await authAxios.put(`/profile/country?country_code=${newCountry}&source=settings`);
      setUser({ ...user, country_confirmed: newCountry });
      const country = SUPPORTED_COUNTRIES.find(c => c.code === newCountry);
      toast.success(`Country set to ${country?.flag || ""} ${country?.name || newCountry}`);
    } catch (error) {
      console.error("Failed to update country:", error);
      toast.error(error.response?.data?.detail || "Failed to update country");
      setCountryConfirmed(user?.country_confirmed || "");
    } finally {
      setSavingCountry(false);
    }
  };

  const handleThemeChange = (newTheme) => {
    if (savingTheme || newTheme === theme) return;
    
    setSavingTheme(true);
    
    try {
      changeTheme(newTheme);
      setUser({ ...user, theme: newTheme });
      
      setTimeout(() => {
        toast.success("Theme updated");
        setSavingTheme(false);
      }, 300);
    } catch (error) {
      console.error('Theme change error:', error);
      toast.error("Failed to update theme");
      setSavingTheme(false);
    }
  };

  // Auto-save settings toggles
  const saveEmailPref = async (key, value) => {
    try {
      await authAxios.put("/profile", { email_prefs: { [key]: value } });
    } catch (error) {
      console.error("Failed to save email preference:", error);
    }
  };

  const handleNotificationsChange = (val) => {
    setNotifications(val);
    saveEmailPref("notifications", val);
  };

  const handleMarketingChange = (val) => {
    setMarketingEmails(val);
    saveEmailPref("marketing", val);
  };

  const handleTicketUpdatesChange = (val) => {
    setTicketUpdateEmails(val);
    saveEmailPref("ticket_updates", val);
  };

  const handleForumRepliesChange = (val) => {
    setForumReplyEmails(val);
    saveEmailPref("forum_replies", val);
  };

  const handleAffiliatePaymentEmailsChange = async (val) => {
    setAffiliatePaymentEmails(val);
    try {
      await authAxios.put("/profile/notification-preferences", {
        affiliate_payment_emails: val,
      });
    } catch (error) {
      // revert on failure
      setAffiliatePaymentEmails(!val);
      toast.error("Failed to update affiliate email preference");
    }
  };

  const handleAffiliateWeeklyDigestChange = async (val) => {
    setAffiliateWeeklyDigest(val);
    try {
      await authAxios.put("/profile/notification-preferences", {
        affiliate_weekly_digest: val,
      });
    } catch (error) {
      setAffiliateWeeklyDigest(!val);
      toast.error("Failed to update weekly digest preference");
    }
  };

  const handleDarkModeChange = (val) => {
    setDarkMode(val);
    authAxios.put("/profile", { dark_mode: val }).catch(() => {});
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      await resetToDefault();
      toast.success("Farewell, divine soul...");
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      return;
    }
    toast.info("Account deletion coming soon");
  };

  const handleRequestPasswordReset = async () => {
    setSendingPasswordReset(true);
    try {
      await authAxios.post("/auth/request-password-change");
      toast.success("Password reset link sent to your email");
      setPasswordResetModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset link");
    } finally {
      setSendingPasswordReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Account Edit Modals */}
      <EditAccountModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} accountData={accountData} onSaved={handleAccountSaved} isCyber={isCyber} />
      <ChangePhoneModal isOpen={phoneModalOpen} onClose={() => setPhoneModalOpen(false)} currentPhone={accountData.phone_number} isCyber={isCyber} />
      <ChangeEmailModal isOpen={emailModalOpen} onClose={() => setEmailModalOpen(false)} isCyber={isCyber} />

      {/* Password Reset Modal */}
      <Dialog open={passwordResetModalOpen} onOpenChange={setPasswordResetModalOpen}>
        <DialogContent className={`${isCyber ? "bg-[#0a0a0f] border-cyan-400/30" : "bg-divine-charcoal border-divine-gold/30"} text-white max-w-md`}>
          <DialogHeader>
            <DialogTitle className={`font-cinzel ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>Change Password</DialogTitle>
            <DialogDescription className="text-white/60">
              A password reset link will be sent to your verified email address: <span className="text-white font-medium">{accountData.email}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              data-testid="password-reset-cancel-btn"
              variant="outline"
              onClick={() => setPasswordResetModalOpen(false)}
              disabled={sendingPasswordReset}
              className={`${isCyber ? "border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10" : "border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"}`}
            >
              Cancel
            </Button>
            <Button
              data-testid="password-reset-confirm-btn"
              onClick={handleRequestPasswordReset}
              disabled={sendingPasswordReset}
              className={`${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold hover:bg-divine-gold/90 text-black"}`}
            >
              {sendingPasswordReset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-white/60 hover:text-white"
              title="Home"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                alt="Agent Eros"
                className="w-6 h-6 object-contain"
                style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
              />
            </Button>
            <div>
              <h1 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>My Account</h1>
            </div>
          </div>
          
          {/* Right side controls */}
          <div className="flex items-center gap-2">
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu exclude="/profile" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 pt-0.5 md:pt-8 pb-8">
        {/* Tabbed Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="sticky top-[60px] z-40 flex md:grid w-auto md:w-full md:max-w-4xl md:grid-cols-4 md:justify-center justify-start bg-divine-charcoal border-y border-divine-gold/20 overflow-x-auto md:overflow-visible whitespace-nowrap rounded-none -mx-6 mb-6 pl-4 pr-4 md:mx-0 md:pl-1 md:pr-1 md:rounded-md md:border md:sticky md:top-[66px] touch-pan-x overscroll-x-contain [will-change:transform]">
            <TabsTrigger 
              value="profile"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="my-persona"
              className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
            >
              <UserCircle className="h-4 w-4 mr-2" />
              My Persona
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="support"
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white relative"
              data-testid="support-tab"
            >
              <Headset className="h-4 w-4 mr-2" />
              Support
              {unreadTicketCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                  {unreadTicketCount > 9 ? "9+" : unreadTicketCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* My Persona Tab */}
          <TabsContent value="my-persona" className="mt-6">
            <div className="space-y-6">
              {/* Persona Selector */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <UserCircle className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-cinzel text-lg text-white">Select Your Persona</h3>
                </div>
                <p className="text-white/60 text-sm mb-4">
                  Choose which relationship persona represents YOU. This persona stores your personal details and bio.
                </p>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-white/80 text-sm">Current Persona</Label>
                    <select
                      value={selectedPersonaId}
                      onChange={(e) => handleSetUserPersona(e.target.value)}
                      disabled={savingPersona}
                      className={`w-full mt-2 bg-divine-black border rounded-md px-3 py-2 text-sm focus:outline-none ${
                        isCyber 
                          ? "border-cyan-400/50 text-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30" 
                          : "border-divine-gold/50 text-white focus:border-divine-gold focus:ring-2 focus:ring-divine-gold/30"
                      }`}
                    >
                      <option value="">-- Select a match --</option>
                      {allMatches.map((match) => (
                        <option key={match.match_id} value={match.match_id}>
                          {match.name} {match.match_type === "user_persona" ? "(Current Persona)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Current Persona Display */}
              {userPersona ? (
                <div className="glass rounded-xl p-6 border-2 border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 to-purple-500/10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-cyan-500/20 ring-2 ring-cyan-400 flex items-center justify-center overflow-hidden">
                        {userPersona.picture ? (
                          <img src={userPersona.picture} alt={userPersona.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-cyan-400 font-cinzel text-3xl">{userPersona.name?.[0] || "?"}</span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-cinzel text-xl text-cyan-400">{userPersona.name}</h3>
                        <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded-full">MY PERSONA</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/relationships/${userPersona.match_id}/edit`)}
                      className="hidden md:inline-flex bg-cyan-500 hover:bg-cyan-400 text-black rounded-full"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Persona
                    </Button>
                  </div>

                  {/* Mobile-only Edit Persona button, sits above Bio */}
                  <Button
                    onClick={() => navigate(`/relationships/${userPersona.match_id}/edit`)}
                    className="md:hidden w-full bg-cyan-500 hover:bg-cyan-400 text-black rounded-full mb-4"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Persona
                  </Button>

                  {/* Persona Details Preview */}
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    {userPersona.bio && (
                      <div className="col-span-2">
                        <Label className="text-white/60 text-xs uppercase">Bio</Label>
                        <p className="text-white/80 mt-1">{userPersona.bio}</p>
                      </div>
                    )}
                    {userPersona.living_location && (
                      <div>
                        <Label className="text-white/60 text-xs uppercase">Location</Label>
                        <p className="text-white/80 mt-1">{userPersona.living_location}</p>
                      </div>
                    )}
                    {userPersona.work_details && (
                      <div>
                        <Label className="text-white/60 text-xs uppercase">Work</Label>
                        <p className="text-white/80 mt-1">{userPersona.work_details}</p>
                      </div>
                    )}
                    {userPersona.hobbies_passions && (
                      <div className="col-span-2">
                        <Label className="text-white/60 text-xs uppercase">Hobbies & Passions</Label>
                        <p className="text-white/80 mt-1">{userPersona.hobbies_passions}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 flex justify-end">
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/relationships/${userPersona.match_id}`)}
                      className="text-cyan-400 hover:bg-cyan-400/10"
                    >
                      View Full Persona
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="glass rounded-xl p-8 text-center">
                  <UserCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <h3 className="font-cinzel text-lg text-white mb-2">No Persona Selected</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Select a match from the dropdown above to set it as your personal persona, or create a new one.
                  </p>
                  <Button
                    onClick={() => navigate("/relationships")}
                    className="bg-cyan-500 hover:bg-cyan-400 text-black rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Persona
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>


          <TabsContent value="profile" className="mt-6">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* Left Column - Avatar & Basic */}
          <div className={`rounded-xl p-6 text-center ${isCyber ? "glass-cyber" : "glass-gold"}`}>
              <div 
                className="relative w-24 h-24 mx-auto mb-4 group cursor-pointer"
                onClick={() => setAvatarModalOpen(true)}
                data-testid="avatar-upload-trigger"
              >
                <div className="w-full h-full rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-divine-gold" />
                  )}
                </div>
                {/* Hover overlay */}
                <div className={`absolute inset-0 rounded-full flex items-center justify-center transition-all ${
                  isCyber 
                    ? "bg-cyan-500/0 group-hover:bg-cyan-500/70" 
                    : "bg-divine-gold/0 group-hover:bg-divine-gold/70"
                }`}>
                  <Camera className={`w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isCyber ? "text-black" : "text-divine-black"
                  }`} />
                </div>
                {/* Edit badge */}
                <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-divine-black ${
                  isCyber ? "bg-cyan-500" : "bg-divine-gold"
                }`}>
                  <Camera className={`w-3.5 h-3.5 ${isCyber ? "text-black" : "text-divine-black"}`} />
                </div>
              </div>
              <h3 className="font-cinzel text-lg text-white">{user?.name}</h3>
              <p className="text-white/40 text-sm">{user?.email}</p>
              <button
                onClick={() => setAvatarModalOpen(true)}
                className={`mt-3 text-xs hover:underline ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}
              >
                Change Photo
              </button>
          </div>

          {/* Right Column - Fast Start Setup */}
          <div className={`md:col-span-2 rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`}>
              <h4 className={`font-cinzel text-lg mb-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                Fast Start Setup
              </h4>
              <div className="space-y-3">
                {/* Persona Profile Task */}
                <button
                  onClick={() => toggleTaskPanel('persona')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isPersonaProfileComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isPersonaProfileComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <span className="text-white/40 text-xs">{userPersona?.profile_completion || 0}%</span>
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isPersonaProfileComplete ? "text-white" : "text-white/60"}`}>
                      Update and Complete your persona profile ({userPersona?.profile_completion || 0}% complete)
                    </span>
                  </div>
                </button>

                {/* Relationship Profile Task */}
                <button
                  onClick={() => toggleTaskPanel('relationship')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isRelationshipProfileComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isRelationshipProfileComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <span className="text-white/40 text-xs">{topRelationshipMatch?.profile_completion || 0}%</span>
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isRelationshipProfileComplete ? "text-white" : "text-white/60"}`}>
                      Update and Complete a relationship profile ({topRelationshipMatch?.profile_completion || 0}% complete)
                    </span>
                  </div>
                </button>

                {/* First Contact with Agent Eros Task */}
                <button
                  onClick={() => toggleTaskPanel('firstContact')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isFirstContactComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isFirstContactComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Sparkles className="w-3 h-3 text-white/40" />
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isFirstContactComplete ? "text-white" : "text-white/60"}`}>
                      First Contact with Agent Eros {isFirstContactComplete ? "(Complete)" : ""}
                    </span>
                  </div>
                </button>

                {/* Learning Library Task */}
                <button
                  onClick={() => toggleTaskPanel('learningLibrary')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isLearningLibraryComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isLearningLibraryComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <span className="text-white/40 text-xs">{learningLibraryCompletion}%</span>
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isLearningLibraryComplete ? "text-white" : "text-white/60"}`}>
                      Create Learning Libraries from Data Sources ({learningLibraryCompletion}% complete)
                    </span>
                  </div>
                </button>

                {/* Style Card Task */}
                <button
                  onClick={() => toggleTaskPanel('styleCard')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isStyleCardComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isStyleCardComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <span className="text-white/40 text-xs">{styleCardCompletion}%</span>
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isStyleCardComplete ? "text-white" : "text-white/60"}`}>
                      Create Communication Style-Card ({styleCardCompletion}% complete)
                    </span>
                  </div>
                </button>

                {/* Compatibility Report Task */}
                <button
                  onClick={() => toggleTaskPanel('compatibilityReport')}
                  className="w-full flex items-center group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isCompatibilityReportComplete ? "bg-yellow-500/20" : "bg-white/10"
                    }`}>
                      {isCompatibilityReportComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Scale className="w-3 h-3 text-white/40" />
                      )}
                    </div>
                    <span className={`text-sm whitespace-normal text-left ${isCompatibilityReportComplete ? "text-white" : "text-white/60"}`}>
                      Analyze Relationship Compatibility {isCompatibilityReportComplete ? "(Complete)" : ""}
                    </span>
                  </div>
                </button>
              </div>
          </div>
        </div>

        {/* Default Panel - Fast Start Setup Overview (when no task selected) */}
        {activeTaskPanel === null && (
          <div className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass-gold border border-divine-gold/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"}`}>
                    <Rocket className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                  </div>
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                      Fast Start Setup
                    </h3>
                    <p className="text-white/50 text-sm">Your journey to deeper connections begins here</p>
                  </div>
                </div>
                {/* Fast Start Complete Claim Button */}
                {!isRewardClaimed('fast_start_complete') && (
                  <button
                    onClick={() => handleClaimCoins('fast_start_complete')}
                    disabled={!isAllTasksComplete || claimingButton === 'fast_start_complete'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isAllTasksComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-fast-start-complete"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.fast_start_complete.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  Agent Eros is a robust, data-driven AI model with deep thinking and deep learning capabilities, 
                  created to help you learn and master the art of meaningful connections. Whether you're navigating 
                  the dating world, strengthening a long-term partnership, or fostering deeper bonds with family and friends, 
                  Agent Eros provides deeply tailored relationship coaching for every situation.
                </p>

                <p className="text-white/60 text-sm leading-relaxed">
                  Agent Eros is here to help you foster deeper meaningful connections, re-activate the love bubble, 
                  navigate relationship challenges, and find "the one" just for you. To accomplish this and unlock 
                  Agent Eros's full potential, your input is essential. The more data you provide, the more powerful 
                  and personalized Agent Eros's guidance becomes.
                </p>

                {/* Total Completion Score */}
                <div className={`rounded-xl p-6 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/30" : "bg-divine-gold/10 border border-divine-gold/30"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                        Overall Progress
                      </h4>
                      <p className="text-white/50 text-sm">Complete all tasks to unlock Agent Eros's full potential</p>
                    </div>
                    <div className={`text-4xl font-bold ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                      {totalFastStartCompletion}%
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-700 ${
                        isCyber 
                          ? "bg-gradient-to-r from-cyan-600 to-cyan-400" 
                          : "bg-gradient-to-r from-divine-gold/70 to-divine-gold"
                      }`}
                      style={{ width: `${totalFastStartCompletion}%` }}
                    />
                  </div>
                  
                  <p className="mt-3 text-white/50 text-sm">
                    {totalFastStartCompletion === 100 
                      ? "Congratulations! You've completed the Fast Start Setup. Agent Eros is now fully equipped to provide you with the best relationship guidance."
                      : totalFastStartCompletion >= 75
                      ? "Excellent progress! You're almost there. Complete the remaining tasks to unlock Agent Eros's full capabilities."
                      : totalFastStartCompletion >= 50
                      ? "Great work! You're halfway through. Keep going to maximize Agent Eros's ability to help you."
                      : totalFastStartCompletion >= 25
                      ? "Good start! Continue setting up to unlock more powerful relationship insights."
                      : "Get started by clicking on any task above to learn more and begin your setup journey."}
                  </p>
                </div>

                {/* What You'll Unlock */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-white/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                  <p className={`text-sm font-medium mb-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                    What Completing Setup Unlocks:
                  </p>
                  <ul className="text-white/70 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <Coins className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-amber-400"}`} />
                      <span>Earn rewards and unlock bonus coins by completing tasks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <span>Personalized coaching tailored to your unique relationship dynamics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <span>Deep compatibility insights based on multiple relationship factors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <span>Communication assistance in your authentic voice and style</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <span>AI-powered roleplay for practicing difficult conversations</span>
                    </li>
                  </ul>
                </div>

                {/* Tech Note */}
                <p className="text-white/40 text-xs leading-relaxed">
                  This level of deep data relationship coaching has only become available as of February 2026, 
                  with the latest advances in AI technology. Your data is securely stored and used exclusively 
                  to enhance your experience with Agent Eros.
                </p>

                {/* Call to Action */}
                <div className="rounded-lg p-4 bg-yellow-500/10 border border-yellow-500/30">
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Ready to Begin?
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        Click on any of the tasks above to learn more about each step and begin your Fast Start Setup journey. 
                        Each task builds on the previous one, helping Agent Eros understand you and your relationships better. 
                        And don't forget to claim your bonus coins!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expandable Persona Info Panel - Full Width */}
        {activeTaskPanel === 'persona' && (
          <div id="fast1" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass-gold border border-divine-gold/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {/* Show avatar if available, otherwise show icon */}
                  {userPersona?.thumbnail_url || userPersona?.picture ? (
                    <div className={`w-12 h-12 rounded-full overflow-hidden border-2 ${isCyber ? "border-cyan-500/50" : "border-divine-gold/50"}`}>
                      <img 
                        src={userPersona.thumbnail_url || userPersona.picture} 
                        alt={userPersona.name || "Your Persona"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"}`}>
                      <UserCircle className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    </div>
                  )}
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                      Complete Your Persona Profile
                    </h3>
                    <p className="text-white/50 text-sm">Help Agent Eros understand you better</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('persona_profile') && (
                  <button
                    onClick={() => handleClaimCoins('persona_profile')}
                    disabled={!isPersonaProfileComplete || claimingButton === 'persona_profile'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isPersonaProfileComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-persona-profile-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.persona_profile.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  To help Agent Eros provide personalized relationship coaching and insights, 
                  it's important to build a complete picture of who you are. The more details 
                  you share about your personality, values, and relationship style, the better 
                  we can tailor advice and match compatibility analysis to your unique needs.
                  You can manually enter your data directly into the profile form, or simply 
                  keep chatting with Agent Eros and more data points will be added to your 
                  profile automatically over time.
                </p>

                {/* Beta Setup Instructions */}
                <div className={`rounded-lg p-5 mb-2 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-gold/10 border border-divine-gold/20"}`} data-testid="beta-setup-block">
                  <h3 className={`text-base font-cinzel font-semibold mb-3 tracking-wider ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                    BETA SETUP
                  </h3>
                  <ol className="space-y-3 text-white/80 text-sm leading-relaxed list-decimal list-inside">
                    <li>
                      Create or Update Your Profile either Manual Entry or AI Assisted for fastest startup.
                    </li>
                    <li>
                      Return here to Fast Start profile and select <span className={`font-semibold ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>PERSONA</span> from Tabs above. From the dropdown select your new persona. This is now your User Bio.
                    </li>
                    <li>
                      Edit, Fine Tune and Update your Bio for maximum accuracy.
                    </li>
                  </ol>
                </div>

                <p className="text-white/60 text-sm">
                  Your profile information is kept private and is only used to enhance your 
                  coaching experience. If anything changes in your life, remember to keep 
                  your profile up to date.
                </p>

                {/* Progress Section */}
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-gold/10 border border-divine-gold/20"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/80 font-medium">Profile Progress</span>
                    <span className={`text-lg font-bold ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                      {userPersona?.profile_completion || 0}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${isCyber ? "bg-cyan-500" : "bg-divine-gold"}`}
                      style={{ width: `${userPersona?.profile_completion || 0}%` }}
                    />
                  </div>
                  
                  {userPersona?.profile_completion === 100 ? (
                    <p className={`mt-3 text-sm flex items-center gap-2 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                      <CheckCircle2 className="w-4 h-4" />
                      Your profile is complete! Keep it updated as things change.
                    </p>
                  ) : (
                    <p className="mt-3 text-white/50 text-sm">
                      {userPersona?.profile_completion >= 80 
                        ? "Almost there! Just a few more details to complete your profile."
                        : userPersona?.profile_completion >= 50
                        ? "Good progress! Continue adding details about yourself."
                        : "Get started by filling in your basic information and personality traits."}
                    </p>
                  )}
                </div>

                {/* Quick Tip */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-yellow-500/10 border border-yellow-500/30"}`}>
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Fast Profile Updates
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        In the Profile Editor, use <span className="text-white/80 font-medium">"AUTO DATA ENTRY"</span> to 
                        paste bios, social media profiles, website about pages, or personal notes. This will automatically 
                        convert them into form fields. Save your raw data in the <span className="text-white/80 font-medium">"Raw Pasted Data"</span> section 
                        at the bottom of the form for future reference.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex justify-center pt-2">
                  <Button
                    onClick={() => userPersona?.match_id && navigate(`/relationships/${userPersona.match_id}/edit`)}
                    disabled={!userPersona?.match_id}
                    className={`px-4 sm:px-8 py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-divine-gold hover:bg-divine-gold-light text-divine-black"
                    }`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    UPDATE YOUR PROFILE
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expandable Relationship Info Panel - Full Width */}
        {activeTaskPanel === 'relationship' && (
          <div id="fast2" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass border border-divine-red/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {/* Show match avatar if available, otherwise show heart icon */}
                  {topRelationshipMatch && (topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture) ? (
                    <div className={`w-12 h-12 rounded-full overflow-hidden border-2 ${isCyber ? "border-cyan-500/50" : "border-divine-red/50"}`}>
                      <img 
                        src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                        alt={topRelationshipMatch.name || "Relationship"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-divine-red/20"}`}>
                      <Heart className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-divine-red"}`} />
                    </div>
                  )}
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-divine-red"}`}>
                      Update and Complete a Relationship Profile
                    </h3>
                    <p className="text-white/50 text-sm">Build deeper connections with detailed profiles</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('relationship_profile') && (
                  <button
                    onClick={() => handleClaimCoins('relationship_profile')}
                    disabled={!isRelationshipProfileComplete || claimingButton === 'relationship_profile'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isRelationshipProfileComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-relationship-profile-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.relationship_profile.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  To help Agent Eros provide personalized relationship coaching, it's important 
                  to build complete profiles for the people in your life. The more details you 
                  share about your relationships, their personalities, and your dynamics together, 
                  the better we can tailor advice and compatibility insights.
                </p>

                <p className="text-white/60 text-sm">
                  Keep your relationship profiles up to date as things change. This helps Agent 
                  Eros track your progress and provide relevant guidance for each stage of your 
                  relationships.
                </p>

                {/* Beta Setup Instructions */}
                <div className={`rounded-lg p-5 mb-2 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-gold/10 border border-divine-gold/20"}`} data-testid="beta-setup-relationship-block">
                  <h3 className={`text-base font-cinzel font-semibold mb-3 tracking-wider ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                    BETA SETUP
                  </h3>
                  <ol className="space-y-3 text-white/80 text-sm leading-relaxed list-decimal list-inside">
                    <li>
                      Create Your first Relationship either Manual Entry or AI Assisted for fastest startup.
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddPersonaModal(true)}
                        className={`ml-2 h-7 px-3 text-xs ${isCyber ? "border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/20" : "border-divine-gold/40 text-divine-gold hover:bg-divine-gold/20"}`}
                        data-testid="beta-add-relationship-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Persona
                      </Button>
                    </li>
                    <li>
                      You can access your relationship profiles from the <a href="/relationships" className={`font-semibold ${isCyber ? "text-cyan-400 hover:text-cyan-300" : "text-divine-gold hover:text-divine-gold/80"} underline`}>Relationships Gallery</a>.
                    </li>
                    <li>
                      Edit, Fine Tune and Update your relationship for maximum accuracy.
                    </li>
                  </ol>
                </div>

                {/* Progress Section */}
                {topRelationshipMatch ? (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-red/10 border border-divine-red/20"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture ? (
                        <img 
                          src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                          alt={topRelationshipMatch.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-divine-red/30"}`}>
                          <span className="text-white text-sm font-medium">{topRelationshipMatch.name?.[0] || "?"}</span>
                        </div>
                      )}
                      <span className="text-white/80 font-medium">{topRelationshipMatch.name || "Unknown"}</span>
                      {topRelationshipMatch.relationship_status && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-divine-red/20 text-divine-red"}`}>
                          {topRelationshipMatch.relationship_status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <span className={`text-lg font-bold ${isCyber ? "text-cyan-400" : "text-divine-red"}`}>
                      {topRelationshipMatch.profile_completion || 0}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${isCyber ? "bg-cyan-500" : "bg-divine-red"}`}
                      style={{ width: `${topRelationshipMatch.profile_completion || 0}%` }}
                    />
                  </div>
                  
                  {topRelationshipMatch.profile_completion === 100 ? (
                    <p className={`mt-3 text-sm flex items-center gap-2 ${isCyber ? "text-cyan-400" : "text-divine-red"}`}>
                      <CheckCircle2 className="w-4 h-4" />
                      This profile is complete! Keep it updated as things change.
                    </p>
                  ) : (
                    <p className="mt-3 text-white/50 text-sm">
                      {topRelationshipMatch.profile_completion >= 80 
                        ? "Almost there! Just a few more details to complete this profile."
                        : topRelationshipMatch.profile_completion >= 50
                        ? "Good progress! Continue adding details about this relationship."
                        : "Get started by filling in basic information and personality traits."}
                    </p>
                  )}
                </div>
                ) : (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-red/10 border border-divine-red/20"}`}>
                  <p className="text-white/60 text-sm">No relationship profile created yet. Use the "+ Add Persona" button above to get started.</p>
                </div>
                )}

                {/* Quick Tip */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-yellow-500/10 border border-yellow-500/30"}`}>
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Fast Profile Updates
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        In the Profile Editor, use <span className="text-white/80 font-medium">"AUTO DATA ENTRY"</span> to 
                        paste bios, dating profiles, social media about pages, or chat logs. This will automatically 
                        convert them into form fields. Save your raw data in the <span className="text-white/80 font-medium">"Raw Pasted Data"</span> section 
                        at the bottom of the form for future reference.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex justify-center pt-2">
                  {topRelationshipMatch ? (
                  <Button
                    onClick={() => navigate(`/relationships/${topRelationshipMatch.match_id}/edit`)}
                    className={`px-4 sm:px-8 py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-divine-red hover:bg-divine-red/80 text-white"
                    }`}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    UPDATE RELATIONSHIP PROFILE
                  </Button>
                  ) : (
                  <Button
                    onClick={() => setShowAddPersonaModal(true)}
                    className={`px-4 sm:px-8 py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-divine-red hover:bg-divine-red/80 text-white"
                    }`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    CREATE YOUR FIRST RELATIONSHIP
                  </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expandable First Contact with Agent Eros Panel - Full Width */}
        {activeTaskPanel === 'firstContact' && (
          <div id="fast3" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass border border-pink-500/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-pink-500/20"}`}>
                    <Sparkles className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                  </div>
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-pink-400"}`}>
                      First Contact with Agent Eros
                    </h3>
                    <p className="text-white/50 text-sm">Your AI relationship coach is ready to meet you</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('first_contact') && (
                  <button
                    onClick={() => handleClaimCoins('first_contact')}
                    disabled={!isFirstContactComplete || claimingButton === 'first_contact'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isFirstContactComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-first-contact-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.first_contact.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  Now that you have your relationship profiles set up, you are ready to meet Agent Eros! 
                  Agent Eros can help assess, guide, and coach you through any relationship dynamic, 
                  from dating to long-term partnerships, marriage, and keeping the love alive.
                </p>

                <p className="text-white/60 text-sm">
                  Whether you need advice on communication, conflict resolution, deepening intimacy, 
                  or navigating difficult conversations, Agent Eros draws on your profile data to 
                  provide personalized, thoughtful guidance tailored to your unique situation.
                </p>

                {/* Key Benefits */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-pink-500/5 border border-pink-500/20"}`}>
                  <p className={`text-sm font-medium mb-3 ${isCyber ? "text-cyan-400" : "text-pink-400"}`}>
                    What Agent Eros Can Help With:
                  </p>
                  <ul className="text-white/70 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                      <span>Relationship assessments and compatibility insights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                      <span>Communication coaching and message crafting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                      <span>Conflict resolution strategies and scripts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                      <span>Date ideas and romantic gesture suggestions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-pink-400"}`} />
                      <span>Understanding your partner's perspective and needs</span>
                    </li>
                  </ul>
                </div>

                {/* Quick Tip */}
                <div className="rounded-lg p-4 bg-yellow-500/10 border border-yellow-500/30">
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Advanced Features
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        You can send images to Agent Eros for assessment, including screenshots of social 
                        media posts when you need help crafting comments or responses. You can also ask 
                        Agent Eros to roleplay as the person in your relationship profile, allowing you 
                        to practice conversations and explore any scenario you can imagine.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Privacy Note */}
                <p className="text-white/40 text-xs">
                  All conversations with Agent Eros are private and saved to your account. 
                  Agent Eros learns from your profile data to provide the most personalized advice possible.
                </p>

                {/* Match Card and Action Button */}
                {topRelationshipMatch ? (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-pink-500/10 border border-pink-500/20"}`}>
                  <div className="flex items-center gap-4 mb-4">
                    {topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture ? (
                      <img 
                        src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                        alt={topRelationshipMatch.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-pink-500/30"}`}>
                        <span className="text-white text-lg font-medium">{topRelationshipMatch.name?.[0] || "?"}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{topRelationshipMatch.name}</p>
                      {topRelationshipMatch.relationship_status && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-pink-500/20 text-pink-400"}`}>
                          {topRelationshipMatch.relationship_status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {isFirstContactComplete && (
                      <CheckCircle2 className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                    )}
                  </div>
                  
                  <Button
                    onClick={async () => {
                      // Mark task complete and navigate
                      await markTaskComplete("first_contact_agent_eros", { match_id: topRelationshipMatch.match_id });
                      navigate(`/eroscoach/${topRelationshipMatch.match_id}`);
                    }}
                    className={`w-full py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-pink-500 hover:bg-pink-400 text-white"
                    }`}
                  >
                    <Sparkles className="w-4 h-4 mr-2 shrink-0 inline" />
                    CHAT WITH AGENT EROS AND {topRelationshipMatch.name?.toUpperCase()}
                  </Button>
                </div>
                ) : (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-pink-500/10 border border-pink-500/20"}`}>
                  <p className="text-white/60 text-sm mb-4">Create a relationship profile first to start chatting with Agent Eros.</p>
                  <Button
                    onClick={() => setShowAddPersonaModal(true)}
                    className={`w-full py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-pink-500 hover:bg-pink-400 text-white"
                    }`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    CREATE YOUR FIRST RELATIONSHIP
                  </Button>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Expandable Learning Library Info Panel - Full Width */}
        {activeTaskPanel === 'learningLibrary' && (
          <div id="fast4" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass border border-purple-500/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-purple-500/20"}`}>
                    <Database className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-purple-400"}`} />
                  </div>
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-purple-400"}`}>
                      Create Learning Libraries
                    </h3>
                    <p className="text-white/50 text-sm">Build AI insights from your Data Sources</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('learning_library') && (
                  <button
                    onClick={() => handleClaimCoins('learning_library')}
                    disabled={!isLearningLibraryComplete || claimingButton === 'learning_library'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isLearningLibraryComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-learning-library-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.learning_library.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  Learning Libraries are AI-generated insight profiles created from your Data Sources. 
                  They help Agent Eros understand communication styles, values, preferences, and personality 
                  traits in depth. This enables more personalized and accurate relationship coaching.
                </p>

                <p className="text-white/60 text-sm">
                  Add Data Sources (bios, chat logs, social profiles, notes) to a profile, then generate 
                  Learning Libraries. The more data you provide, the richer the insights.
                </p>

                {/* How to Generate Instructions */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-white/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                  <p className={`text-sm font-medium mb-2 ${isCyber ? "text-cyan-400" : "text-purple-400"}`}>
                    How to Generate:
                  </p>
                  <ol className="text-white/70 text-sm space-y-2 list-decimal list-inside">
                    <li>Click one of the buttons below to open Data Sources for a profile</li>
                    <li>Create or add a new Data Source (bio, chat log, notes, etc.)</li>
                    <li>Select your new Data Source from the list. You can also select <span className="text-white/90 font-medium">"CORE_MATCH_DATA"</span> or <span className="text-white/90 font-medium">"CORE_USER_DATA"</span></li>
                    <li>Once selected, a <span className="text-white/90 font-medium">"GENERATE LEARNING LIBRARY"</span> button will appear at the top of the list</li>
                    <li>Click to generate. This may take a few minutes depending on data size</li>
                    <li>Once complete, review results in the <span className="text-white/90 font-medium">Learning Library</span> section</li>
                  </ol>
                </div>

                {/* Progress Section */}
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-purple-500/10 border border-purple-500/20"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/80 font-medium">Learning Library Progress</span>
                    <span className={`text-lg font-bold ${isCyber ? "text-cyan-400" : "text-purple-400"}`}>
                      {learningLibraryCompletion}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${isCyber ? "bg-cyan-500" : "bg-purple-500"}`}
                      style={{ width: `${learningLibraryCompletion}%` }}
                    />
                  </div>
                  
                  <p className="mt-3 text-white/50 text-sm">
                    {learningLibraryCompletion === 100 
                      ? "All Learning Libraries created! Keep them updated as you add new Data Sources."
                      : learningLibraryCompletion >= 50
                      ? "Good progress! Create Learning Libraries for your remaining profiles."
                      : "Get started by adding Data Sources and generating Learning Libraries."}
                  </p>
                </div>

                {/* Two sections with avatars and buttons */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Your Profile Data Sources */}
                  {userPersona && (
                    <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {userPersona.thumbnail_url || userPersona.picture ? (
                          <img 
                            src={userPersona.thumbnail_url || userPersona.picture} 
                            alt={userPersona.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-divine-gold/30"}`}>
                            <span className="text-white text-sm font-medium">{userPersona.name?.[0] || "?"}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{userPersona.name}</p>
                          <p className="text-white/40 text-xs">Your Profile</p>
                        </div>
                        {userPersonaHasLearning ? (
                          <CheckCircle2 className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white/10" />
                        )}
                      </div>
                      <Button
                        onClick={() => navigate(`/relationships/${userPersona.match_id}/?tab=data-sources`)}
                        variant="outline"
                        className={`w-full text-sm ${
                          isCyber 
                            ? "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                            : "border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        }`}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        YOUR PROFILE DATA SOURCES
                      </Button>
                    </div>
                  )}

                  {/* Relationship Data Sources */}
                  {topRelationshipMatch && (
                    <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture ? (
                          <img 
                            src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                            alt={topRelationshipMatch.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-divine-red/30"}`}>
                            <span className="text-white text-sm font-medium">{topRelationshipMatch.name?.[0] || "?"}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{topRelationshipMatch.name}</p>
                          <p className="text-white/40 text-xs">Relationship</p>
                        </div>
                        {topMatchHasLearning ? (
                          <CheckCircle2 className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white/10" />
                        )}
                      </div>
                      <Button
                        onClick={() => navigate(`/relationships/${topRelationshipMatch.match_id}/?tab=data-sources`)}
                        variant="outline"
                        className={`w-full text-sm ${
                          isCyber 
                            ? "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                            : "border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        }`}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        RELATIONSHIP DATA SOURCES
                      </Button>
                    </div>
                  )}
                </div>

                {/* Quick Tip */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-yellow-500/10 border border-yellow-500/30"}`}>
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Building Better Learning Libraries
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        In the Data Sources tab, add multiple sources such as chat logs, social bios, dating profiles, and 
                        personal notes. Then click <span className="text-white/80 font-medium">"Generate Learning Library"</span> to 
                        create AI insight profiles. More diverse sources = richer insights.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expandable Style Card Info Panel - Full Width */}
        {activeTaskPanel === 'styleCard' && (
          <div id="fast5" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass border border-orange-500/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-orange-500/20"}`}>
                    <Palette className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-orange-400"}`} />
                  </div>
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-orange-400"}`}>
                      Create Communication Style-Card
                    </h3>
                    <p className="text-white/50 text-sm">Capture your unique communication voice</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('style_card') && (
                  <button
                    onClick={() => handleClaimCoins('style_card')}
                    disabled={!isStyleCardComplete || claimingButton === 'style_card'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isStyleCardComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-style-card-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.style_card.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  To fine-tune communication style, Agent Eros can generate messages in your own personal voice, 
                  as well as roleplay communications in the style of any relationship profile. This helps Agent Eros 
                  craft authentic messages that sound like you, and understand how others communicate.
                </p>

                <p className="text-white/60 text-sm">
                  You can use any type of written communication to build a style card: chat logs, copies of messages, 
                  emails, social media posts, or any other communication samples.
                </p>

                {/* Step-by-Step Instructions */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-white/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                  <p className={`text-sm font-medium mb-3 ${isCyber ? "text-cyan-400" : "text-orange-400"}`}>
                    How to Create a Style-Card:
                  </p>
                  <ol className="text-white/70 text-sm space-y-2 list-decimal list-inside">
                    <li>In the <span className="text-white/90 font-medium">Data Sources</span> section of a profile, click the <span className="text-white/90 font-medium">"ADD NEW"</span> button</li>
                    <li>In the Add Data Source window, enable the <span className="text-white/90 font-medium">"Analyze Communication Style"</span> toggle</li>
                    <li>Paste the chat log into the Content field, ensuring names are correctly identified in the text</li>
                    <li>Save the data source</li>
                    <li>In the Data Source list, select any existing <span className="text-white/90 font-medium">STYLE_CARD</span> file along with the new chat log</li>
                    <li>Click the <span className="text-white/90 font-medium">"Generate Communication Style-Card"</span> button at the top of the list</li>
                  </ol>
                </div>

                {/* Update Info */}
                <p className="text-white/60 text-sm">
                  This generates a <span className="text-white/80 font-medium">MATCH_STYLE_CARD</span> or <span className="text-white/80 font-medium">USER_STYLE_CARD</span> data 
                  resource. To refine the style card over time, repeat these steps with additional communication samples, building on previous style cards.
                </p>

                {/* Progress Section */}
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-orange-500/10 border border-orange-500/20"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/80 font-medium">Style-Card Progress</span>
                    <span className={`text-lg font-bold ${isCyber ? "text-cyan-400" : "text-orange-400"}`}>
                      {styleCardCompletion}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${isCyber ? "bg-cyan-500" : "bg-orange-500"}`}
                      style={{ width: `${styleCardCompletion}%` }}
                    />
                  </div>
                  
                  <p className="mt-3 text-white/50 text-sm">
                    {styleCardCompletion === 100 
                      ? "All style cards created! Keep them updated as you add new communication samples."
                      : styleCardCompletion >= 50
                      ? "Good progress! Create a style card for your remaining profile."
                      : "Get started by adding communication samples and generating style cards."}
                  </p>
                </div>

                {/* Two Profile Cards */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Your Profile */}
                  {userPersona && (
                    <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {userPersona.thumbnail_url || userPersona.picture ? (
                          <img 
                            src={userPersona.thumbnail_url || userPersona.picture} 
                            alt={userPersona.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-divine-gold/30"}`}>
                            <span className="text-white text-sm font-medium">{userPersona.name?.[0] || "?"}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{userPersona.name}</p>
                          <p className="text-white/40 text-xs">Your Profile</p>
                        </div>
                        {userPersonaHasStyleCard ? (
                          <CheckCircle2 className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white/10" />
                        )}
                      </div>
                      <Button
                        onClick={() => navigate(`/relationships/${userPersona.match_id}/`)}
                        variant="outline"
                        className={`w-full text-sm ${
                          isCyber 
                            ? "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                            : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        }`}
                      >
                        <User className="w-4 h-4 mr-2" />
                        YOUR PROFILE
                      </Button>
                    </div>
                  )}

                  {/* Relationship Profile */}
                  {topRelationshipMatch && (
                    <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture ? (
                          <img 
                            src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                            alt={topRelationshipMatch.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-divine-red/30"}`}>
                            <span className="text-white text-sm font-medium">{topRelationshipMatch.name?.[0] || "?"}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{topRelationshipMatch.name}</p>
                          <p className="text-white/40 text-xs">Relationship</p>
                        </div>
                        {topMatchHasStyleCard ? (
                          <CheckCircle2 className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white/10" />
                        )}
                      </div>
                      <Button
                        onClick={() => navigate(`/relationships/${topRelationshipMatch.match_id}/`)}
                        variant="outline"
                        className={`w-full text-sm ${
                          isCyber 
                            ? "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                            : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        }`}
                      >
                        <User className="w-4 h-4 mr-2" />
                        {topRelationshipMatch.name?.toUpperCase()} PROFILE
                      </Button>
                    </div>
                  )}
                </div>

                {/* Quick Tip */}
                <div className="rounded-lg p-4 bg-yellow-500/10 border border-yellow-500/30">
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Reusing Chat Logs
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        If you have a chat log between yourself and a relationship, you can use the same log for both 
                        profiles. Just ensure the names in the chat log match the respective persona names so Agent Eros 
                        can correctly identify who each message belongs to.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expandable Compatibility Report Info Panel - Full Width */}
        {activeTaskPanel === 'compatibilityReport' && (
          <div id="fast6" className={`mt-6 rounded-xl p-4 sm:p-8 overflow-hidden scroll-mt-28 ${isCyber ? "glass-cyber border border-cyan-500/30" : "glass border border-emerald-500/30"}`}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-emerald-500/20"}`}>
                    <Scale className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                  </div>
                  <div>
                    <h3 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-emerald-400"}`}>
                      Generate A Relationship Compatibility Report
                    </h3>
                    <p className="text-white/50 text-sm">Discover your relationship dynamics</p>
                  </div>
                </div>
                {/* Claim Button */}
                {!isRewardClaimed('compatibility') && (
                  <button
                    onClick={() => handleClaimCoins('compatibility')}
                    disabled={!isCompatibilityReportComplete || claimingButton === 'compatibility'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isCompatibilityReportComplete 
                        ? "bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 cursor-pointer animate-pulse"
                        : "bg-white/5 border border-white/10 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid="claim-compatibility-panel"
                  >
                    <Coins className="w-4 h-4" />
                    <span>{FAST_START_REWARDS.compatibility.coins.toLocaleString()}</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <p className="text-white/80 leading-relaxed">
                  Agent Eros can analyze relationship compatibility based on multiple factors, with a primary focus on 
                  shared resonance. This includes complementary interests, love languages, attachment styles, astrology, 
                  relationship types, and current relationship status.
                </p>

                <p className="text-white/60 text-sm">
                  The compatibility report helps identify potential challenges, strengths, and areas for growth in your 
                  relationship. It provides high-level relationship guidance that can be incorporated into coaching sessions 
                  for even deeper, more personalized insights.
                </p>

                {/* How to Generate */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-white/5 border border-cyan-500/20" : "bg-white/5 border border-white/10"}`}>
                  <p className={`text-sm font-medium mb-3 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`}>
                    How to Generate:
                  </p>
                  <ol className="text-white/70 text-sm space-y-2 list-decimal list-inside">
                    <li>Navigate to the relationship profile page</li>
                    <li>Click the <span className="text-white/90 font-medium">"ANALYZE COMPATIBILITY"</span> button</li>
                    <li>Wait a couple of minutes for processing to complete</li>
                    <li>Review the comprehensive compatibility report</li>
                  </ol>
                </div>

                {/* What's Included */}
                <div className={`rounded-lg p-4 ${isCyber ? "bg-cyan-500/5 border border-cyan-500/20" : "bg-emerald-500/5 border border-emerald-500/20"}`}>
                  <p className={`text-sm font-medium mb-3 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`}>
                    What the Report Analyzes:
                  </p>
                  <ul className="text-white/70 text-sm space-y-1.5">
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                      <span>Love languages and emotional needs alignment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                      <span>Attachment styles and relationship patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                      <span>Shared interests and lifestyle compatibility</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                      <span>Astrological insights and cosmic compatibility</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isCyber ? "text-cyan-400" : "text-emerald-400"}`} />
                      <span>Communication style synergies</span>
                    </li>
                  </ul>
                </div>

                {/* Additional Info */}
                <p className="text-white/50 text-sm">
                  Reports can be regenerated as profiles are updated with new information, providing fresh insights 
                  as your relationship evolves.
                </p>

                {/* Profile Card */}
                {topRelationshipMatch ? (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-emerald-500/10 border border-emerald-500/20"}`}>
                  <div className="flex items-center gap-4 mb-4">
                    {topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture ? (
                      <img 
                        src={topRelationshipMatch.thumbnail_url || topRelationshipMatch.picture} 
                        alt={topRelationshipMatch.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isCyber ? "bg-cyan-500/30" : "bg-emerald-500/30"}`}>
                        <span className="text-white text-lg font-medium">{topRelationshipMatch.name?.[0] || "?"}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{topRelationshipMatch.name}</p>
                      {topRelationshipMatch.relationship_status && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                          {topRelationshipMatch.relationship_status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {topMatchHasCompatibilityReport && (
                      <CheckCircle2 className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
                    )}
                  </div>
                  
                  <Button
                    onClick={() => navigate(`/relationships/${topRelationshipMatch.match_id}/`)}
                    className={`w-full py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-emerald-500 hover:bg-emerald-400 text-white"
                    }`}
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    {topRelationshipMatch.name?.toUpperCase()}'S PROFILE
                  </Button>
                </div>
                ) : (
                <div className={`rounded-lg p-5 ${isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-emerald-500/10 border border-emerald-500/20"}`}>
                  <p className="text-white/60 text-sm mb-4">Create a relationship profile first to generate a compatibility report.</p>
                  <Button
                    onClick={() => setShowAddPersonaModal(true)}
                    className={`w-full py-3 rounded-full font-medium transition-all whitespace-normal text-center break-words ${
                      isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-emerald-500 hover:bg-emerald-400 text-white"
                    }`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    CREATE YOUR FIRST RELATIONSHIP
                  </Button>
                </div>
                )}

                {/* Quick Tip */}
                <div className="rounded-lg p-4 bg-yellow-500/10 border border-yellow-500/30">
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium mb-1 text-yellow-400">
                        Quick Tip: Beyond Romantic Relationships
                      </p>
                      <p className="text-white/60 text-sm leading-relaxed">
                        Compatibility reports aren't limited to romantic relationships. Agent Eros can analyze 
                        compatibility for friendships, family members, siblings, work colleagues, and any other 
                        type of relationship you want to understand better.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Subscription Panel */}
              <SubscriptionSettingsPanel isCyber={isCyber} />

              {/* Account Section — Enhanced with Phone, State, Country */}
              <div className={`rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`} data-testid="account-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Account</h3>
                  <Button
                    data-testid="edit-account-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditModalOpen(true)}
                    className={`rounded-full text-xs px-3 h-8 ${isCyber ? "border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10" : "border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"}`}
                  >
                    <Pencil className="w-3 h-3 mr-1.5" />
                    Edit
                  </Button>
                </div>
                
                {/* User avatar + name */}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg mb-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"}`}>
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>{accountData.name?.[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate" data-testid="account-name">{accountData.name}</p>
                    <p className="text-white/40 text-sm truncate">{accountData.state && accountData.country ? `${accountData.state}, ${accountData.country}` : accountData.country || accountData.state || "Location not set"}</p>
                    <p className="text-white/30 text-xs mt-1 font-mono">{user?.user_id}</p>
                  </div>
                </div>

                {/* Detail rows */}
                <div className="space-y-3">
                  {/* Email */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Mail className={`w-4 h-4 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div className="min-w-0">
                        <p className="text-white/50 text-xs">Email</p>
                        <p className="text-white text-sm truncate" data-testid="account-email">{accountData.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-amber-400 text-[10px] bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="email-verification-badge">Requires Verification</span>
                      <button onClick={() => setEmailModalOpen(true)} className={`p-1 ${isCyber ? "text-cyan-400 hover:text-cyan-300" : "text-divine-gold hover:text-divine-gold/80"}`} data-testid="change-email-btn">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Phone */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Phone className={`w-4 h-4 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div className="min-w-0">
                        <p className="text-white/50 text-xs">Phone</p>
                        <p className={`text-sm truncate ${accountData.phone_number ? "text-white" : "text-white/30"}`} data-testid="account-phone">{accountData.phone_number || "Not set"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-amber-400 text-[10px] bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="phone-verification-badge">Requires Verification</span>
                      <button onClick={() => setPhoneModalOpen(true)} className={`p-1 ${isCyber ? "text-cyan-400 hover:text-cyan-300" : "text-divine-gold hover:text-divine-gold/80"}`} data-testid="change-phone-btn">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* State */}
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <MapPin className={`w-4 h-4 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    <div className="min-w-0">
                      <p className="text-white/50 text-xs">State / Province</p>
                      <p className={`text-sm truncate ${accountData.state ? "text-white" : "text-white/30"}`} data-testid="account-state">{accountData.state || "Not set"}</p>
                    </div>
                  </div>
                  {/* Country */}
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Globe className={`w-4 h-4 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    <div className="min-w-0">
                      <p className="text-white/50 text-xs">Country</p>
                      <p className={`text-sm truncate ${accountData.country ? "text-white" : "text-white/30"}`} data-testid="account-country">{accountData.country || "Not set"}</p>
                    </div>
                  </div>
                  {/* Change Password */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <KeyRound className={`w-4 h-4 shrink-0 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white/50 text-xs">Password</p>
                        <p className="text-white/30 text-sm">••••••••</p>
                      </div>
                    </div>
                    <Button
                      data-testid="change-password-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => setPasswordResetModalOpen(true)}
                      className={`rounded-full text-xs px-3 h-7 ${isCyber ? "border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10" : "border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"}`}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              </div>

              {/* Change History Audit Log */}
              <ChangeHistoryPanel isCyber={isCyber} />

              {/* Preferences */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-cinzel text-lg text-white mb-4">Preferences</h3>
                
                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Palette className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Theme</p>
                        <p className="text-white/40 text-sm">Choose your visual experience</p>
                      </div>
                    </div>
                    <Select 
                      value={theme} 
                      onValueChange={handleThemeChange}
                      disabled={savingTheme}
                    >
                      <SelectTrigger className={`w-full md:w-[180px] bg-divine-black border text-white ${isCyber ? "border-cyan-400/30" : "border-divine-gold/30"}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={`border ${isCyber ? "bg-[#0a0a0f] border-cyan-400/30" : "bg-divine-charcoal border-divine-gold/30"}`}>
                        <SelectItem value="ancient_rome">Ancient Rome</SelectItem>
                        <SelectItem value="agent_eros_1">Agent Eros 1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Languages className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Language</p>
                        <p className="text-white/40 text-sm">Choose your preferred language</p>
                      </div>
                    </div>
                    <Select 
                      value={language} 
                      onValueChange={handleLanguageChange}
                      disabled={savingLanguage}
                    >
                      <SelectTrigger className={`w-full md:w-[150px] bg-divine-black border text-white ${isCyber ? "border-cyan-400/30" : "border-divine-gold/30"}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={`border ${isCyber ? "bg-[#0a0a0f] border-cyan-400/30" : "bg-divine-charcoal border-divine-gold/30"}`}>
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="french">Français</SelectItem>
                        <SelectItem value="spanish">Español</SelectItem>
                        <SelectItem value="german">Deutsch</SelectItem>
                        <SelectItem value="italian">Italiano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Globe className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Timezone</p>
                        <p className="text-white/40 text-sm">For displaying log timestamps</p>
                      </div>
                    </div>
                    <Select 
                      value={timezone} 
                      onValueChange={handleTimezoneChange}
                      disabled={savingTimezone}
                    >
                      <SelectTrigger className={`w-full md:w-[200px] bg-divine-black border text-white ${isCyber ? "border-cyan-400/30" : "border-divine-gold/30"}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={`border max-h-80 ${isCyber ? "bg-[#0a0a0f] border-cyan-400/30" : "bg-divine-charcoal border-divine-gold/30"}`}>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="pt-2 pb-1 border-b border-white/10">
                    <h4 className="text-white/80 font-cinzel text-sm tracking-wide flex items-center gap-2">
                      <Bell className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      E-Mail Notifications
                    </h4>
                    <p className="text-white/40 text-xs mt-0.5">Choose which emails you receive from Agent Eros</p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Special Offers & Marketing</p>
                        <p className="text-white/40 text-sm">Receive special offers and marketing info</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="marketing-emails-switch"
                      checked={marketingEmails}
                      onCheckedChange={handleMarketingChange}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Headset className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Support Ticket Updates</p>
                        <p className="text-white/40 text-sm">Receive support ticket updates via email</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="ticket-updates-switch"
                      checked={ticketUpdateEmails}
                      onCheckedChange={handleTicketUpdatesChange}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <MessageSquare className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Forum Reply Notifications</p>
                        <p className="text-white/40 text-sm">Email me when someone replies to my forum posts</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="forum-replies-switch"
                      checked={forumReplyEmails}
                      onCheckedChange={handleForumRepliesChange}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Coins className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Affiliate Payment Emails</p>
                        <p className="text-white/40 text-sm">Email me when an affiliate payout clears into my Stripe account</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="affiliate-payment-emails-switch"
                      checked={affiliatePaymentEmails}
                      onCheckedChange={handleAffiliatePaymentEmailsChange}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Weekly Earnings Digest</p>
                        <p className="text-white/40 text-sm">Every Friday morning — your weekly commissions, referrals, and payouts snapshot</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="affiliate-weekly-digest-switch"
                      checked={affiliateWeeklyDigest}
                      onCheckedChange={handleAffiliateWeeklyDigestChange}
                    />
                  </div>

                </div>
              </div>

              {/* Terms & Conditions — user's accepted Beta T&C snapshot */}
              <TcAgreementCard isCyber={isCyber} />

              {/* Legal Documents — public reference links */}
              <div className={`rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`} data-testid="legal-documents-card">
                <h3 className={`font-cinzel text-lg mb-4 flex items-center gap-2 ${isCyber ? "text-cyan-400" : "text-white"}`}>
                  <FileText className="w-4 h-4" />
                  Legal Documents
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-beta-terms"
                    className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white text-sm font-medium">Beta Terms &amp; Conditions</p>
                        <p className="text-white/40 text-xs">NDA + Beta Terms of Use — public reference copy</p>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-white/40 group-hover:text-white/70" />
                  </Link>
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-privacy-statement"
                    className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white text-sm font-medium">Agent Eros Privacy Statement</p>
                        <p className="text-white/40 text-xs">How we collect, use, and protect your data</p>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-white/40 group-hover:text-white/70" />
                  </Link>
                </div>
              </div>

              {/* Privacy */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-cinzel text-lg text-white mb-4">Privacy & Security</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Data Privacy</p>
                        <p className="text-white/40 text-sm">Your data is encrypted and secure</p>
                      </div>
                    </div>
                    <span className="text-green-500 text-sm">Protected</span>
                  </div>
                </div>
              </div>

              {/* Safety & Location */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-cinzel text-lg text-white mb-4">Safety & Location</h3>
                <p className="text-white/50 text-sm mb-4">
                  Your location helps us provide the correct emergency and crisis resources if you ever need support.
                </p>
                
                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <MapPin className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      <div>
                        <p className="text-white">Your Country</p>
                        <p className="text-white/40 text-sm">For crisis resources & emergency numbers</p>
                      </div>
                    </div>
                    <Select 
                      value={countryConfirmed || "not_set"} 
                      onValueChange={(val) => val !== "not_set" && handleCountryChange(val)}
                      disabled={savingCountry}
                    >
                      <SelectTrigger 
                        className={`w-full md:w-[200px] bg-divine-black border text-white ${isCyber ? "border-cyan-400/30" : "border-divine-gold/30"}`}
                        data-testid="country-select"
                      >
                        <SelectValue>
                          {countryConfirmed ? (
                            <>
                              {SUPPORTED_COUNTRIES.find(c => c.code === countryConfirmed)?.flag}{" "}
                              {SUPPORTED_COUNTRIES.find(c => c.code === countryConfirmed)?.name || countryConfirmed}
                            </>
                          ) : (
                            <span className="text-white/50">Select country...</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className={`border max-h-80 ${isCyber ? "bg-[#0a0a0f] border-cyan-400/30" : "bg-divine-charcoal border-divine-gold/30"}`}>
                        {countryConfirmed && (
                          <SelectItem value="clear" className="text-divine-red">
                            ✕ Clear selection
                          </SelectItem>
                        )}
                        {SUPPORTED_COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {countryConfirmed && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Crisis resources will use {SUPPORTED_COUNTRIES.find(c => c.code === countryConfirmed)?.name || countryConfirmed} numbers
                      </p>
                    </div>
                  )}
                  
                  {!countryConfirmed && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-amber-400 text-sm">
                        No country set. If you need crisis support, we&apos;ll ask your location first.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-cinzel text-lg text-white mb-4">Actions</h3>
                
                <div className="space-y-3">
                  {user?.role && ["admin", "support"].includes(user.role) && (
                    <Button
                      onClick={() => navigate("/admin")}
                      variant="outline"
                      className={`w-full justify-start ${isCyber ? "border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10" : "border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"}`}
                    >
                      <UserCog className="w-5 h-5 mr-3" />
                      Admin Panel
                    </Button>
                  )}
                  
                  <Button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/5 justify-start"
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    Sign Out
                  </Button>
                  
                  <Button
                    data-testid="delete-account-btn"
                    onClick={handleDeleteAccount}
                    variant="outline"
                    className="w-full border-divine-red/50 text-divine-red hover:bg-divine-red/10 justify-start"
                  >
                    <Trash2 className="w-5 h-5 mr-3" />
                    Delete Account
                  </Button>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center py-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-20 h-20 mx-auto mb-2 object-contain"
                />
                <p className="text-white/40 text-sm">Agent Eros v1.1b</p>
                <p className="text-white/20 text-xs mt-1">Forged with divine inspiration</p>
              </div>
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="mt-6">
            <div className="space-y-6">
              {/* Support Header */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Headset className="w-5 h-5 text-purple-400" />
                  <h3 className="font-cinzel text-lg text-white">Support Tickets</h3>
                </div>
                <div className="mb-3">
                  <Button
                    onClick={() => navigate("/contact")}
                    className={isCyber 
                      ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                      : "bg-purple-500 hover:bg-purple-600 text-white"
                    }
                    data-testid="new-ticket-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Request
                  </Button>
                </div>
                <p className="text-white/60 text-sm">
                  View and manage your support requests. Need help?{" "}
                  <button 
                    onClick={() => navigate("/contact")}
                    className="text-purple-400 hover:underline"
                  >
                    Submit a new request
                  </button>
                </p>
              </div>
              
              {/* Ticket Detail View */}
              {selectedTicket ? (
                <div className="glass rounded-xl p-6">
                  {/* Back button */}
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="flex items-center gap-2 text-white/60 hover:text-white mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to tickets
                  </button>
                  
                  {/* Ticket header */}
                  <div className="border-b border-white/10 pb-4 mb-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-cinzel text-lg text-white">{selectedTicket.subject}</h4>
                        <div className="flex items-center gap-3 mt-2 text-sm flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedTicket.status === "OPEN" ? "bg-blue-500/20 text-blue-400" :
                            selectedTicket.status === "IN_PROGRESS" ? "bg-amber-500/20 text-amber-400" :
                            selectedTicket.status === "RESOLVED" ? "bg-green-500/20 text-green-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`} data-testid="ticket-detail-status-badge">
                            {selectedTicket.status}
                          </span>
                          <span className="text-white/50">{selectedTicket.category}</span>
                          <span className="text-white/40">
                            {new Date(selectedTicket.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-white/50 text-xs whitespace-nowrap">Set status:</span>
                        <Select
                          value={["OPEN", "CLOSED", "RESOLVED"].includes(selectedTicket.status) ? selectedTicket.status : ""}
                          onValueChange={handleTicketStatusChange}
                          disabled={updatingTicketStatus}
                        >
                          <SelectTrigger
                            className={`w-[130px] h-8 text-xs ${isCyber
                              ? "bg-[#0a0a0f] border-cyan-500/30 text-cyan-100 hover:border-cyan-400/60 focus:ring-cyan-400/40"
                              : "bg-[#1a1a1a] border-purple-500/30 text-white hover:border-purple-400/60 focus:ring-purple-400/40"
                            }`}
                            data-testid="ticket-status-change-trigger"
                          >
                            <SelectValue placeholder={selectedTicket.status === "IN_PROGRESS" ? "In progress" : "Change…"} />
                          </SelectTrigger>
                          <SelectContent
                            className={isCyber
                              ? "bg-[#0a0a0f] border-cyan-500/30 text-cyan-100"
                              : "bg-[#161616] border-purple-500/30 text-white"
                            }
                          >
                            <SelectItem value="OPEN" data-testid="ticket-status-open">Open</SelectItem>
                            <SelectItem value="RESOLVED" data-testid="ticket-status-resolved">Resolved</SelectItem>
                            <SelectItem value="CLOSED" data-testid="ticket-status-closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-white/40 text-xs hidden sm:inline">{selectedTicket.ticket_id}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Messages */}
                  <div className="space-y-4 max-h-[400px] overflow-y-auto mb-4">
                    {selectedTicket.messages?.map((msg, idx) => (
                      <div 
                        key={msg.message_id || idx}
                        className={`p-4 rounded-lg ${
                          msg.sender_type === "USER" 
                            ? "bg-white/5 ml-8" 
                            : "bg-purple-500/10 mr-8 border-l-2 border-purple-500"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${
                            msg.sender_type === "ADMIN" ? "text-purple-400" : "text-white/80"
                          }`}>
                            {msg.sender_type === "ADMIN" ? "Support Team" : "You"}
                          </span>
                          <span className="text-white/40 text-xs">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {msg.attachments.map((att, i) => (
                              <a 
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-20 h-20 rounded-lg overflow-hidden border border-white/20 hover:border-purple-400 transition-colors"
                              >
                                <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Reply form (only if not closed) */}
                  {selectedTicket.status !== "CLOSED" && (
                    <div className="border-t border-white/10 pt-4">
                      <Label className="text-white/80 text-sm">Reply</Label>
                      <Textarea
                        value={ticketReply}
                        onChange={(e) => setTicketReply(e.target.value)}
                        placeholder="Type your reply..."
                        className="mt-2 !bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40"
                        data-testid="ticket-reply-input"
                      />
                      <div className="flex items-center justify-between mt-3">
                        <ImageAttachPicker
                          attachments={replyAttachments}
                          setAttachments={setReplyAttachments}
                          disabled={sendingReply}
                        />
                        <Button
                          onClick={handleTicketReply}
                          disabled={sendingReply || !ticketReply.trim()}
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                          data-testid="send-reply-btn"
                        >
                          {sendingReply ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Send Reply
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Tickets List */
                <div className="glass rounded-xl p-6">
                  {/* Filter dropdown */}
                  <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">Show:</span>
                      <Select
                        value={ticketStatusFilter}
                        onValueChange={setTicketStatusFilter}
                      >
                        <SelectTrigger
                          className={`w-[180px] h-9 text-sm ${isCyber
                            ? "bg-[#0a0a0f] border-cyan-500/30 text-cyan-100 hover:border-cyan-400/60 focus:ring-cyan-400/40"
                            : "bg-[#1a1a1a] border-purple-500/30 text-white hover:border-purple-400/60 focus:ring-purple-400/40"
                          }`}
                          data-testid="ticket-filter-trigger"
                        >
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent
                          className={isCyber
                            ? "bg-[#0a0a0f] border-cyan-500/30 text-cyan-100"
                            : "bg-[#161616] border-purple-500/30 text-white"
                          }
                        >
                          <SelectItem value="ALL" data-testid="ticket-filter-all">All tickets</SelectItem>
                          <SelectItem value="OPEN" data-testid="ticket-filter-open">Open</SelectItem>
                          <SelectItem value="IN_PROGRESS" data-testid="ticket-filter-in-progress">In progress</SelectItem>
                          <SelectItem value="RESOLVED" data-testid="ticket-filter-resolved">Resolved</SelectItem>
                          <SelectItem value="CLOSED" data-testid="ticket-filter-closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3">
                      {supportTickets.some(t => t.unread_by_user) && (
                        <button
                          onClick={handleMarkAllTicketsRead}
                          disabled={markingAllRead}
                          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium transition-colors border ${isCyber
                            ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400/60"
                            : "bg-purple-500/10 border-purple-500/40 text-purple-300 hover:bg-purple-500/20 hover:border-purple-400/60"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          data-testid="mark-all-read-btn"
                        >
                          <CheckCheck className={`w-3.5 h-3.5 ${markingAllRead ? "animate-pulse" : ""}`} />
                          {markingAllRead ? "Marking…" : "Mark all as read"}
                        </button>
                      )}
                      <span className="text-white/40 text-xs" data-testid="ticket-filter-count">
                        {(() => {
                          const shown = ticketStatusFilter === "ALL"
                            ? supportTickets.length
                            : supportTickets.filter(t => t.status === ticketStatusFilter).length;
                          return `${shown} of ${supportTickets.length}`;
                        })()}
                      </span>
                    </div>
                  </div>

                  {loadingSupportTickets ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
                    </div>
                  ) : supportTickets.length === 0 ? (
                    <div className="text-center py-12">
                      <Headset className="w-12 h-12 mx-auto mb-4 text-white/20" />
                      <p className="text-white/50 mb-4">No support tickets yet</p>
                      <Button
                        onClick={() => navigate("/contact")}
                        variant="outline"
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        Contact Support
                      </Button>
                    </div>
                  ) : (() => {
                    const visibleTickets = ticketStatusFilter === "ALL"
                      ? supportTickets
                      : supportTickets.filter(t => t.status === ticketStatusFilter);
                    if (visibleTickets.length === 0) {
                      return (
                        <div className="text-center py-12" data-testid="ticket-filter-empty">
                          <Headset className="w-12 h-12 mx-auto mb-4 text-white/20" />
                          <p className="text-white/50">
                            No {ticketStatusFilter.toLowerCase().replace("_", " ")} tickets
                          </p>
                        </div>
                      );
                    }
                    return (
                    <div className="space-y-3">
                      {visibleTickets.map((ticket) => (
                        <button
                          key={ticket.ticket_id}
                          onClick={() => fetchTicketDetail(ticket.ticket_id)}
                          className="w-full text-left p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                          data-testid={`ticket-${ticket.ticket_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {ticket.unread_by_user && (
                                  <span
                                    className="w-2 h-2 bg-red-500 rounded-full shrink-0"
                                    data-testid={`ticket-unread-dot-${ticket.ticket_id}`}
                                    aria-label="Unread reply"
                                  />
                                )}
                                <h4 className="font-medium text-white truncate">{ticket.subject}</h4>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-sm flex-wrap">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  ticket.status === "OPEN" ? "bg-blue-500/20 text-blue-400" :
                                  ticket.status === "IN_PROGRESS" ? "bg-amber-500/20 text-amber-400" :
                                  ticket.status === "RESOLVED" ? "bg-green-500/20 text-green-400" :
                                  "bg-gray-500/20 text-gray-400"
                                }`}>
                                  {ticket.status}
                                </span>
                                <span className="text-white/50">{ticket.category}</span>
                                {typeof ticket.message_count === "number" && (
                                  <span
                                    className="inline-flex items-center gap-1 text-white/40 text-xs"
                                    data-testid={`ticket-reply-count-${ticket.ticket_id}`}
                                  >
                                    <MessageSquare className="w-3 h-3" />
                                    {ticket.message_count === 1
                                      ? "1 message"
                                      : `${ticket.message_count} messages`}
                                  </span>
                                )}
                                <span className="inline-flex items-center gap-1 text-white/40 text-xs">
                                  <Clock className="w-3 h-3" />
                                  {new Date(ticket.updated_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center text-white/40">
                              <ChevronRight className="w-4 h-4" />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Avatar Upload Modal */}
      <Dialog open={avatarModalOpen} onOpenChange={setAvatarModalOpen}>
        <DialogContent className={`sm:max-w-md ${isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black-light border-divine-gold/30"}`}>
          <DialogHeader>
            <DialogTitle className={`font-cinzel ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
              {isCyber ? "Upload Avatar" : "Update Your Divine Image"}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {isCyber 
                ? "Upload a profile picture to personalize your account" 
                : "Choose a photo that captures your essence"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Preview Area */}
            <div className="flex flex-col items-center">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center overflow-hidden border-2 ${
                isCyber ? "border-cyan-500/50 bg-cyan-500/10" : "border-divine-gold/50 bg-divine-gold/10"
              }`}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className={`w-12 h-12 ${isCyber ? "text-cyan-500/50" : "text-divine-gold/50"}`} />
                )}
              </div>
              {avatarPreview && (
                <button
                  onClick={() => setAvatarPreview(null)}
                  className="mt-2 text-xs text-white/40 hover:text-white flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear selection
                </button>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex flex-col items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="avatar-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className={`w-full ${
                  isCyber 
                    ? "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                    : "border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"
                }`}
                data-testid="select-image-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Select Image
              </Button>
              <p className="text-xs text-white/40">
                Supports JPG, PNG, GIF, WebP • Max 5MB
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {user?.picture && !avatarPreview && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleRemoveAvatar}
                disabled={uploadingAvatar}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="remove-avatar-btn"
              >
                Remove Photo
              </Button>
            )}
            <div className="flex gap-2 sm:ml-auto">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setAvatarModalOpen(false);
                  setAvatarPreview(null);
                }}
                className="text-white/60 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAvatarUpload}
                disabled={!avatarPreview || uploadingAvatar}
                className={`${
                  isCyber 
                    ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                    : "bg-divine-gold hover:bg-divine-gold-light text-black"
                }`}
                data-testid="upload-avatar-btn"
              >
                {uploadingAvatar ? "Uploading..." : "Save Photo"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Coin Purchase Modal */}
      <CoinPurchaseModal
        isOpen={showCoinPurchaseModal}
        onClose={() => setShowCoinPurchaseModal(false)}
        currentBalance={coinBalance}
        onPurchaseComplete={(newBalance) => setCoinBalance(newBalance)}
      />
      <AddPersonaModal
        isOpen={showAddPersonaModal}
        onClose={() => setShowAddPersonaModal(false)}
        existingMatches={allMatches}
        onPersonaAdded={() => { setShowAddPersonaModal(false); fetchAllMatches(); }}
      />
    </div>
  );
};

const CompletionItem = ({ label, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${done ? "bg-divine-gold" : "bg-white/10"}`}>
      {done && <span className="text-black text-xs">✓</span>}
    </div>
    <span className={done ? "text-white" : "text-white/40"}>{label}</span>
  </div>
);

export default ProfileSetup;
